import { useEffect, useRef, useState, useCallback } from "react";
import { BrowserRouter, useLocation, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { ArrowLeft, ArrowRight, ArrowUpRight, AudioLines, BookmarkPlus, Check, ChevronRight, Copy, FileText, Gift, Globe2, Headphones, LifeBuoy, ListMusic, Lock, LogOut, Mic, Paperclip, Pin, Play, Plus, Printer, Radio, Receipt, ShieldCheck, Sparkles, Square, Trash2, Upload, UserRound, Users, X, Zap } from "lucide-react";
import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionKey = () => { try { let k = localStorage.getItem("intervia_sk"); if (!k) { k = "sk_" + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("intervia_sk", k); } return k; } catch { return ""; } };
const track = (event, meta) => { try { fetch(`${API}/telemetry/event`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ event, session_key: getSessionKey(), meta: meta || {}, path: window.location.pathname + window.location.search }), keepalive: true }).catch(() => {}); } catch {} };

const loadRazorpayScript = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => resolve(true); s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

function AuthCallback({ onAuth }) {
  const navigate = useNavigate();
  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    if (!sessionId) { navigate("/", { replace: true }); return; }
    fetch(`${API}/auth/google-session`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ session_id: sessionId }) })
      .then(r => r.ok ? r.json() : Promise.reject(new Error("sign-in-failed")))
      .then(user => { onAuth?.(user); })
      .catch(() => { try { toast.error("Google sign-in couldn't complete — please try again"); } catch {} })
      .finally(() => navigate("/", { replace: true }));
  }, [navigate, onAuth]);
  return <div className="callback" data-testid="auth-callback-status"><Sparkles size={18} /> Syncing your workspace…</div>;
}

function AuthPanel({ onAuth, defaultReferralCode }) {
  const [mode, setMode] = useState("login"); const [email, setEmail] = useState(""); const [password, setPassword] = useState(""); const [name, setName] = useState(""); const [referralCode, setReferralCode] = useState(defaultReferralCode || ""); const [busy, setBusy] = useState(false); const [referrerName, setReferrerName] = useState("");
  useEffect(() => { if (defaultReferralCode) setMode("register"); }, [defaultReferralCode]);
  useEffect(() => { const code = referralCode.trim().toUpperCase(); if (mode !== "register" || code.length < 6) { setReferrerName(""); return; } const t = setTimeout(() => { fetch(`${API}/referrals/lookup/${code}`).then(r => r.ok ? r.json() : null).then(d => setReferrerName(d?.referrer_name || "")).catch(() => setReferrerName("")); }, 400); return () => clearTimeout(t); }, [referralCode, mode]);
  const submit = async (e) => { e.preventDefault(); setBusy(true); try { const body = { email, password, name }; if (mode === "register" && referralCode.trim()) body.referral_code = referralCode.trim().toUpperCase(); const r = await fetch(`${API}/auth/${mode === "login" ? "login" : "register"}`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(body) }); const data = await r.json(); if (!r.ok) throw new Error(data.detail); onAuth(data); toast.success(mode === "login" ? "Welcome back" : "Workspace created"); } catch (err) { toast.error(err.message); } finally { setBusy(false); } };
  const google = () => { const redirectUrl = window.location.origin + "/"; window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`; };
  return <aside className="auth-panel" data-testid="auth-panel">
    <div className="auth-mark"><Sparkles size={15} /> PRIVATE WORKSPACE</div>
    <h2>{mode === "login" ? "Pick up where you left off." : "Build your interview edge."}</h2>
    <p className="muted">Your resume, job context, and practice sessions stay together.</p>
    <form onSubmit={submit}>
      {mode === "register" && <input data-testid="auth-name-input" placeholder="Your name" value={name} onChange={e => setName(e.target.value)} />}
      <input data-testid="auth-email-input" type="email" placeholder="Email address" value={email} onChange={e => setEmail(e.target.value)} required />
      <input data-testid="auth-password-input" type="password" placeholder="Password (8+ characters)" value={password} onChange={e => setPassword(e.target.value)} required />
      {mode === "register" && <input data-testid="auth-referral-input" placeholder="Referral code (optional)" value={referralCode} onChange={e => setReferralCode(e.target.value.toUpperCase())} />}
      {referrerName && <div className="referral-hint" data-testid="referral-hint"><Gift size={12} /> {referrerName} sent you — you'll get +1 bonus session on your first purchase</div>}
      <button className="primary wide" data-testid="auth-submit-button" disabled={busy}>{busy ? "Opening…" : mode === "login" ? "Enter workspace" : "Create free account"}<ChevronRight size={16} /></button>
    </form>
    <div className="or"><span>or</span></div>
    <button className="google-btn" data-testid="google-login-button" onClick={google}><Globe2 size={16} /> Continue with Google</button>
    <button className="text-btn" data-testid="auth-mode-toggle" onClick={() => setMode(mode === "login" ? "register" : "login")}>{mode === "login" ? "New here? Create a free account" : "Already have an account? Sign in"}</button>
  </aside>;
}

function LiveCapture({ onTranscript, disabled }) {
  const [mode, setMode] = useState("idle"); // idle | mic | tab
  const [busy, setBusy] = useState(false);
  const streamRef = useRef(null); const recorderRef = useRef(null); const activeRef = useRef(false); const cycleRef = useRef(null);

  const stop = useCallback(() => {
    activeRef.current = false;
    clearTimeout(cycleRef.current);
    try { recorderRef.current?.state === "recording" && recorderRef.current.stop(); } catch {}
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null; recorderRef.current = null;
    setMode("idle");
  }, []);

  const uploadChunk = async (blob) => {
    if (!blob || blob.size < 2000) return;
    const form = new FormData(); form.append("file", blob, "chunk.webm");
    try {
      const r = await fetch(`${API}/copilot/transcribe`, { method: "POST", credentials: "include", body: form });
      const d = await r.json();
      if (d.text) onTranscript(d.text);
    } catch {}
  };

  const cycleRecorder = useCallback(() => {
    if (!activeRef.current || !streamRef.current) return;
    let mime = "audio/webm;codecs=opus"; if (!window.MediaRecorder?.isTypeSupported(mime)) mime = "audio/webm";
    const rec = new MediaRecorder(streamRef.current, { mimeType: mime });
    const chunks = [];
    rec.ondataavailable = e => e.data.size > 0 && chunks.push(e.data);
    rec.onstop = () => { const blob = new Blob(chunks, { type: mime }); uploadChunk(blob); if (activeRef.current) cycleRecorder(); };
    recorderRef.current = rec;
    rec.start();
    cycleRef.current = setTimeout(() => { try { rec.state === "recording" && rec.stop(); } catch {} }, 5000);
  }, []);

  const start = async (source) => {
    if (disabled) return toast.error("Sign in to use live capture");
    setBusy(true);
    try {
      const stream = source === "tab"
        ? await navigator.mediaDevices.getDisplayMedia({ audio: true, video: true })
        : await navigator.mediaDevices.getUserMedia({ audio: true });
      if (source === "tab" && !stream.getAudioTracks().length) { stream.getTracks().forEach(t => t.stop()); throw new Error("No tab audio — enable 'Share tab audio' in the picker"); }
      streamRef.current = stream; activeRef.current = true; setMode(source);
      stream.getAudioTracks()[0].addEventListener("ended", stop);
      cycleRecorder();
      toast.success(source === "tab" ? "Listening to tab audio" : "Microphone armed");
    } catch (e) { toast.error(e.message || "Could not start capture"); } finally { setBusy(false); }
  };

  useEffect(() => stop, [stop]);

  return <div className="live-capture" data-testid="live-capture">
    <div className="capture-status">
      <span className={mode === "idle" ? "capture-dot" : "capture-dot on"} />
      <strong>{mode === "idle" ? "Standby" : mode === "mic" ? "Microphone live" : "Tab audio live"}</strong>
      <small>Whisper transcribes every ~5 s. Intervia auto-detects questions and streams answers continuously — no need to click Send.</small>
    </div>
    <div className="capture-actions">
      {mode === "idle" ? <>
        <button className="capture-btn" data-testid="start-mic-button" onClick={() => start("mic")} disabled={busy || disabled}><Mic size={14} /> Start mic</button>
        <button className="capture-btn" data-testid="start-tab-button" onClick={() => start("tab")} disabled={busy || disabled}><Headphones size={14} /> Share tab audio</button>
      </> : <button className="capture-btn stop" data-testid="stop-capture-button" onClick={stop}><Square size={14} /> Stop capture</button>}
    </div>
  </div>;
}

function BillingHub({ user, onOpenReceipt }) {
  const [data, setData] = useState(null);
  const [funnel, setFunnel] = useState(null);
  useEffect(() => { if (!user) return; fetch(`${API}/billing/history`, { credentials: "include" }).then(r => r.ok ? r.json() : null).then(setData); fetch(`${API}/telemetry/funnel`, { credentials: "include" }).then(r => r.ok ? r.json() : null).then(setFunnel); }, [user]);
  if (!user) return <main className="pricing-page"><p className="muted">Sign in to view your billing history.</p></main>;
  if (!data) return <main className="pricing-page"><p className="muted">Loading billing…</p></main>;
  const paidTx = data.transactions.filter(t => t.payment_status === "paid");
  const step = (b) => `${b?.unique || 0} · ${b?.count || 0} events`;
  return <main className="pricing-page">
    <div className="eyebrow"><span className="live-line" /> BILLING · CREDITS · RECEIPTS</div>
    <h1>Every credit, <em>traced.</em></h1>
    <p className="pricing-lead">Live balance, purchase history, printable receipts, and the buy-flow funnel that got you here.</p>
    <div className="billing-summary" data-testid="billing-summary">
      <div className="summary-tile"><span>Sessions remaining</span><strong data-testid="summary-remaining">{data.sessions_remaining}</strong></div>
      <div className="summary-tile"><span>Purchases</span><strong>{paidTx.length}</strong></div>
      <div className="summary-tile"><span>Ledger entries</span><strong>{data.ledger.length}</strong></div>
    </div>
    {funnel && <section className="billing-section funnel-section" data-testid="buy-funnel">
      <h3>Buy-flow funnel <span className="funnel-rate" data-testid="funnel-conversion">{funnel.conversion_rate_pct}% conversion</span></h3>
      <div className="funnel-grid">
        <div className="funnel-step"><span>1 · Buy-intent landings</span><strong>{funnel.buy.landing_view_buy_intent.unique}</strong><small>{funnel.buy.landing_view_buy_intent.count} events</small></div>
        <div className="funnel-step"><span>2 · Buy CTA clicks</span><strong>{funnel.buy.buy_cta_click.unique}</strong><small>{funnel.buy.buy_cta_click.count} events</small></div>
        <div className="funnel-step"><span>3 · Signups via buy</span><strong>{funnel.buy.signup_buy_intent.unique}</strong><small>{funnel.buy.signup_buy_intent.count} events</small></div>
        <div className="funnel-step"><span>4 · Checkouts opened</span><strong>{funnel.buy.checkout_opened_buy_intent.unique}</strong><small>{funnel.buy.checkout_opened_buy_intent.count} events</small></div>
        <div className="funnel-step paid"><span>5 · Paid ✓</span><strong>{funnel.buy.checkout_paid_buy_intent.unique}</strong><small>{funnel.buy.checkout_paid_buy_intent.count} events</small></div>
      </div>
      <details className="funnel-try" data-testid="try-funnel"><summary>Try-first funnel</summary>
        <div className="funnel-grid compact">
          <div className="funnel-step"><span>Landings</span><strong>{funnel.try.landing_view.unique}</strong></div>
          <div className="funnel-step"><span>Try CTA</span><strong>{funnel.try.try_cta_click.unique}</strong></div>
          <div className="funnel-step"><span>Signups</span><strong>{funnel.try.signup_try_intent.unique}</strong></div>
          <div className="funnel-step"><span>Checkouts</span><strong>{funnel.try.checkout_opened_try_intent.unique}</strong></div>
          <div className="funnel-step paid"><span>Paid</span><strong>{funnel.try.checkout_paid_try_intent.unique}</strong></div>
        </div>
      </details>
    </section>}
    <section className="billing-section">
      <h3>Purchases</h3>
      {paidTx.length === 0 ? <p className="muted">No purchases yet. Head to Session packs to buy your first pass.</p> : <table className="billing-table" data-testid="billing-transactions"><thead><tr><th>Date</th><th>Product</th><th>Amount</th><th>Status</th><th></th></tr></thead><tbody>{paidTx.map(t => <tr key={t.order_id}><td>{new Date(t.paid_at || t.updated_at).toLocaleString()}</td><td>10-Session Pass</td><td>{t.currency === "INR" ? "₹" : "$"}{(t.amount/100).toLocaleString(undefined, {minimumFractionDigits: 2})}</td><td><span className="pill green">PAID</span></td><td><button className="ghost-action" data-testid={`receipt-btn-${t.order_id}`} onClick={() => onOpenReceipt(t.order_id)}><Receipt size={13} /> Receipt</button></td></tr>)}</tbody></table>}
    </section>
    <section className="billing-section">
      <h3>Credit ledger</h3>
      {data.ledger.length === 0 ? <p className="muted">No credits earned or spent yet.</p> : <table className="billing-table" data-testid="billing-ledger"><thead><tr><th>Date</th><th>Change</th><th>Reason</th></tr></thead><tbody>{data.ledger.map((l, i) => <tr key={i}><td>{new Date(l.created_at).toLocaleString()}</td><td className={l.delta > 0 ? "delta-pos" : l.delta < 0 ? "delta-neg" : "delta-zero"}>{l.delta > 0 ? `+${l.delta}` : l.delta === 0 ? "—" : l.delta}</td><td>{l.reason.replaceAll("_", " ")}</td></tr>)}</tbody></table>}
    </section>
  </main>;
}

function ReceiptModal({ orderId, onClose }) {
  const [receipt, setReceipt] = useState(null);
  useEffect(() => { fetch(`${API}/billing/receipt/${orderId}`, { credentials: "include" }).then(r => r.ok ? r.json() : null).then(d => setReceipt(d?.receipt)); }, [orderId]);
  if (!receipt) return <div className="modal-scrim" onClick={onClose}><div className="receipt-modal" onClick={e => e.stopPropagation()}>Loading…</div></div>;
  return <div className="modal-scrim" data-testid="receipt-modal" onClick={onClose}>
    <div className="receipt-modal" onClick={e => e.stopPropagation()}>
      <div className="receipt-head no-print">
        <strong>Receipt {receipt.receipt_no}</strong>
        <div><button className="ghost-action" data-testid="print-receipt-button" onClick={() => window.print()}><Printer size={13} /> Print / Save PDF</button><button className="ghost-action" onClick={onClose}>Close</button></div>
      </div>
      <div className="receipt-body" id="printable-receipt">
        <div className="receipt-brand"><Zap size={16} /> Intervia</div>
        <div className="receipt-title">Payment receipt</div>
        <div className="receipt-grid">
          <div><span>Receipt #</span><strong>{receipt.receipt_no}</strong></div>
          <div><span>Paid on</span><strong>{new Date(receipt.paid_at).toLocaleString()}</strong></div>
          <div><span>Customer</span><strong>{receipt.customer.name}<br /><em>{receipt.customer.email}</em></strong></div>
          <div><span>Payment method</span><strong>{receipt.provider === "razorpay" ? "Razorpay" : receipt.provider}</strong></div>
          <div><span>Order ID</span><strong>{receipt.order_id}</strong></div>
          <div><span>Payment ID</span><strong>{receipt.payment_id}</strong></div>
        </div>
        <table className="receipt-table"><thead><tr><th>Item</th><th>Qty</th><th>Amount</th></tr></thead><tbody><tr><td>{receipt.product}</td><td>{receipt.sessions_granted} sessions</td><td>{receipt.amount_display}</td></tr></tbody><tfoot><tr><td colSpan="2">Total paid</td><td><strong>{receipt.amount_display}</strong></td></tr></tfoot></table>
        <div className="receipt-footer">Thank you for practising with Intervia. Need help? Visit Contact Support in your workspace.</div>
      </div>
    </div>
  </div>;
}

function ReferralPanel({ user }) {
  const [stats, setStats] = useState(null);
  useEffect(() => { if (!user) return; fetch(`${API}/referrals/me`, { credentials: "include" }).then(r => r.ok ? r.json() : null).then(setStats); }, [user]);
  if (!user) return <main className="pricing-page"><p className="muted">Sign in to see your referral link.</p></main>;
  if (!stats) return <main className="pricing-page"><p className="muted">Loading referrals…</p></main>;
  const shareUrl = `${window.location.origin}/?ref=${stats.code}`;
  const copy = () => { navigator.clipboard?.writeText(shareUrl); toast.success("Referral link copied"); };
  return <main className="pricing-page">
    <div className="eyebrow"><span className="live-line" /> INVITE · EARN · PRACTISE TOGETHER</div>
    <h1>Give a session, <em>get a session.</em></h1>
    <p className="pricing-lead">Every friend who buys their first pack sends you one bonus session. You both win.</p>
    <div className="referral-card" data-testid="referral-card">
      <div className="referral-code-row">
        <div><span>Your referral code</span><strong data-testid="referral-code">{stats.code}</strong></div>
        <div className="referral-link"><input readOnly value={shareUrl} data-testid="referral-share-url" /><button className="primary" data-testid="copy-referral-button" onClick={copy}><Copy size={14} /> Copy link</button></div>
      </div>
      <div className="referral-stats">
        <div><Users size={16} /><span>Signups</span><strong data-testid="referral-signups">{stats.signups}</strong></div>
        <div><Gift size={16} /><span>Purchases converted</span><strong data-testid="referral-conversions">{stats.conversions}</strong></div>
        <div><Sparkles size={16} /><span>Bonus sessions earned</span><strong data-testid="referral-earned">{stats.credits_earned} / {stats.cap}</strong></div>
      </div>
      <p className="muted" data-testid="referral-cap-note">You can earn up to {stats.cap} bonus sessions from referrals. {stats.remaining} still available.</p>
    </div>
  </main>;
}

const PIN_TAGS = [
  { key: "behavioural", label: "Behavioural", color: "#45d7ff" },
  { key: "technical", label: "Technical", color: "#9b8cff" },
  { key: "culture", label: "Culture-fit", color: "#f6be5f" },
];
const TAG_LABEL = Object.fromEntries(PIN_TAGS.map(t => [t.key, t.label]));

function CheatSheet({ user }) {
  const [pins, setPins] = useState(null);
  const [filter, setFilter] = useState("all");
  const load = useCallback(() => { if (!user) return; fetch(`${API}/pins`, { credentials: "include" }).then(r => r.ok ? r.json() : { pins: [] }).then(d => setPins(d.pins || [])); }, [user]);
  useEffect(() => { load(); }, [load]);
  const remove = async (pin_id) => { const r = await fetch(`${API}/pins/${pin_id}`, { method: "DELETE", credentials: "include" }); if (r.ok) { setPins(p => p.filter(x => x.pin_id !== pin_id)); toast.success("Removed from cheat sheet"); } };
  const retag = async (pin_id, tag) => {
    const r = await fetch(`${API}/pins/${pin_id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ tag }) });
    if (!r.ok) return toast.error("Could not update tag");
    setPins(p => p.map(x => x.pin_id === pin_id ? { ...x, tag } : x));
    toast.success(`Moved to ${TAG_LABEL[tag]}`);
  };
  if (!user) return <main className="pricing-page"><p className="muted">Sign in to build your cheat sheet.</p></main>;
  if (pins === null) return <main className="pricing-page"><p className="muted">Loading cheat sheet…</p></main>;
  const filtered = filter === "all" ? pins : pins.filter(p => (p.tag || "behavioural") === filter);
  const counts = PIN_TAGS.reduce((acc, t) => ({ ...acc, [t.key]: pins.filter(p => (p.tag || "behavioural") === t.key).length }), {});
  return <main className="pricing-page">
    <div className="eyebrow"><span className="live-line" /> PINNED ANSWERS · REHEARSAL CHEAT SHEET</div>
    <h1>Your rehearsed <em>best lines.</em></h1>
    <p className="pricing-lead">Every answer you pin lands here. Filter by category right before the round that needs it.</p>
    <div className="tag-tabs" data-testid="tag-tabs">
      <button className={filter === "all" ? "tag-tab active" : "tag-tab"} data-testid="tag-filter-all" onClick={() => setFilter("all")}>All <span>{pins.length}</span></button>
      {PIN_TAGS.map(t => <button key={t.key} className={filter === t.key ? "tag-tab active" : "tag-tab"} style={filter === t.key ? { borderColor: t.color, color: t.color } : {}} data-testid={`tag-filter-${t.key}`} onClick={() => setFilter(t.key)}>{t.label} <span>{counts[t.key]}</span></button>)}
    </div>
    {filtered.length === 0 ? <div className="cheat-empty" data-testid="cheat-empty"><Pin size={30} /><p>{pins.length === 0 ? "No pins yet." : `No ${filter} pins yet.`}</p><small>{pins.length === 0 ? "Ask the copilot a question, then hit the Pin button to add it here." : "Try another filter, or tag existing pins from the cards above."}</small></div> : <div className="cheat-list" data-testid="cheat-list">{filtered.map(p => { const tag = p.tag || "behavioural"; const color = PIN_TAGS.find(t => t.key === tag)?.color || "#45d7ff"; return <article className="cheat-card" data-testid={`pin-${p.pin_id}`} key={p.pin_id}>
      <div className="cheat-head"><span className="pin-kicker" style={{ color }}><Pin size={12} /> PINNED</span><span className="cheat-date">{new Date(p.created_at).toLocaleString()}</span><select className="tag-select" data-testid={`pin-tag-select-${p.pin_id}`} value={tag} onChange={e => retag(p.pin_id, e.target.value)} style={{ borderColor: color, color }}>{PIN_TAGS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}</select><button className="icon-btn danger" data-testid={`pin-delete-${p.pin_id}`} onClick={() => remove(p.pin_id)} title="Remove pin"><Trash2 size={14} /></button></div>
      <div className="cheat-question">Q · {p.question}</div>
      <div className="cheat-answer">{p.answer}</div>
      <button className="ghost-action" data-testid={`pin-copy-${p.pin_id}`} onClick={() => { navigator.clipboard?.writeText(p.answer); toast.success("Answer copied"); }}><Copy size={12} /> Copy</button>
    </article>; })}</div>}
  </main>;
}

function RehearsalModal({ playlist, pins, onClose }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => { const h = (e) => { if (e.key === "Escape") onClose(); if (e.key === "ArrowRight") setIdx(i => Math.min(i + 1, pins.length - 1)); if (e.key === "ArrowLeft") setIdx(i => Math.max(i - 1, 0)); }; window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h); }, [pins.length, onClose]);
  if (!pins.length) return null;
  const p = pins[idx]; const tag = p.tag || "behavioural";
  const color = PIN_TAGS.find(t => t.key === tag)?.color || "#45d7ff";
  return <div className="rehearsal-scrim" data-testid="rehearsal-modal">
    <button className="rehearsal-close" data-testid="rehearsal-close" onClick={onClose}><X size={16} /></button>
    <div className="rehearsal-header"><span className="pin-kicker" style={{ color }}><Play size={12} /> REHEARSAL · {playlist.name}</span><span className="rehearsal-progress" data-testid="rehearsal-progress">{idx + 1} / {pins.length}</span></div>
    <article className="rehearsal-card" data-testid={`rehearsal-card-${p.pin_id}`}>
      <span className="rehearsal-tag" style={{ borderColor: color, color }}>{TAG_LABEL[tag]}</span>
      <div className="rehearsal-question">{p.question}</div>
      <div className="rehearsal-answer">{p.answer}</div>
    </article>
    <div className="rehearsal-controls">
      <button className="capture-btn" data-testid="rehearsal-prev" disabled={idx === 0} onClick={() => setIdx(i => Math.max(i - 1, 0))}><ArrowLeft size={14} /> Prev</button>
      <button className="capture-btn" data-testid="rehearsal-next" disabled={idx === pins.length - 1} onClick={() => setIdx(i => Math.min(i + 1, pins.length - 1))}>Next <ArrowRight size={14} /></button>
    </div>
    <div className="rehearsal-hint">← / → to navigate · Esc to exit</div>
  </div>;
}

function Playlists({ user }) {
  const [lists, setLists] = useState(null);
  const [active, setActive] = useState(null); // {playlist, pins}
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [rehearsing, setRehearsing] = useState(false);
  const [allPins, setAllPins] = useState([]);

  const loadLists = useCallback(async () => { if (!user) return; const r = await fetch(`${API}/playlists`, { credentials: "include" }); const d = r.ok ? await r.json() : { playlists: [] }; setLists(d.playlists || []); }, [user]);
  const loadDetail = useCallback(async (playlist_id) => { const r = await fetch(`${API}/playlists/${playlist_id}`, { credentials: "include" }); if (r.ok) setActive(await r.json()); }, []);
  const loadAllPins = useCallback(async () => { const r = await fetch(`${API}/pins`, { credentials: "include" }); if (r.ok) { const d = await r.json(); setAllPins(d.pins || []); } }, []);

  useEffect(() => { loadLists(); loadAllPins(); }, [loadLists, loadAllPins]);

  const create = async (e) => { e.preventDefault(); const n = name.trim(); if (!n) return toast.error("Give the playlist a name"); setCreating(true); try { const r = await fetch(`${API}/playlists`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ name: n }) }); const d = await r.json(); if (!r.ok) throw new Error(d.detail || "Could not create"); setName(""); await loadLists(); toast.success(`Created "${n}"`); loadDetail(d.playlist_id); } catch (err) { toast.error(err.message); } finally { setCreating(false); } };

  const remove = async (playlist_id) => { if (!window.confirm("Delete this playlist? Pins are not deleted.")) return; const r = await fetch(`${API}/playlists/${playlist_id}`, { method: "DELETE", credentials: "include" }); if (r.ok) { setLists(l => l.filter(x => x.playlist_id !== playlist_id)); if (active?.playlist?.playlist_id === playlist_id) setActive(null); toast.success("Playlist deleted"); } };

  const setMembership = async (pin_ids) => {
    if (!active) return;
    const r = await fetch(`${API}/playlists/${active.playlist.playlist_id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ pin_ids }) });
    const d = await r.json(); if (!r.ok) return toast.error(d.detail || "Update failed");
    await loadDetail(active.playlist.playlist_id); await loadLists();
  };
  const addPin = (pin_id) => { const current = active.playlist.pin_ids || []; if (current.includes(pin_id)) return; setMembership([...current, pin_id]); };
  const removePin = (pin_id) => { const current = active.playlist.pin_ids || []; setMembership(current.filter(x => x !== pin_id)); };
  const move = (idx, dir) => { const list = [...(active.playlist.pin_ids || [])]; const j = idx + dir; if (j < 0 || j >= list.length) return; [list[idx], list[j]] = [list[j], list[idx]]; setMembership(list); };

  if (!user) return <main className="pricing-page"><p className="muted">Sign in to build playlists.</p></main>;
  if (lists === null) return <main className="pricing-page"><p className="muted">Loading playlists…</p></main>;

  const available = active ? allPins.filter(p => !(active.playlist.pin_ids || []).includes(p.pin_id)) : [];

  return <main className="pricing-page">
    <div className="eyebrow"><span className="live-line" /> REHEARSAL · PLAYLISTS</div>
    <h1>Practise in <em>focus mode.</em></h1>
    <p className="pricing-lead">Group your best pinned answers into a named playlist and rehearse them one card at a time before the round.</p>

    <form className="playlist-create" data-testid="playlist-create-form" onSubmit={create}>
      <input data-testid="playlist-name-input" placeholder="Playlist name (e.g., Google phone screen)" value={name} onChange={e => setName(e.target.value)} />
      <button className="primary" data-testid="create-playlist-button" disabled={creating}><Plus size={14} /> {creating ? "Creating…" : "New playlist"}</button>
    </form>

    <div className="playlists-grid" data-testid="playlists-grid">
      {lists.length === 0 && <p className="muted" data-testid="playlists-empty">No playlists yet. Name your first rehearsal set above.</p>}
      {lists.map(l => <button key={l.playlist_id} className={active?.playlist?.playlist_id === l.playlist_id ? "playlist-tile active" : "playlist-tile"} data-testid={`playlist-tile-${l.playlist_id}`} onClick={() => loadDetail(l.playlist_id)}>
        <ListMusic size={14} />
        <div><strong>{l.name}</strong><span>{l.pin_count} pin{l.pin_count === 1 ? "" : "s"}</span></div>
      </button>)}
    </div>

    {active && <section className="playlist-detail" data-testid="playlist-detail">
      <header>
        <h3>{active.playlist.name}</h3>
        <div>
          <button className="primary" data-testid="start-rehearsal-button" disabled={!active.pins.length} onClick={() => setRehearsing(true)}><Play size={14} /> Start rehearsal</button>
          <button className="ghost-action" data-testid={`delete-playlist-${active.playlist.playlist_id}`} onClick={() => remove(active.playlist.playlist_id)}><Trash2 size={13} /> Delete playlist</button>
        </div>
      </header>
      <div className="playlist-columns">
        <div>
          <h4>In this playlist ({active.pins.length})</h4>
          {active.pins.length === 0 ? <p className="muted">No pins added yet. Pick from the right column.</p> : <ol className="playlist-pins" data-testid="playlist-pins">
            {active.pins.map((p, i) => { const tag = p.tag || "behavioural"; const color = PIN_TAGS.find(t => t.key === tag)?.color || "#45d7ff"; return <li key={p.pin_id} data-testid={`playlist-pin-${p.pin_id}`}>
              <span className="playlist-tag" style={{ borderColor: color, color }}>{TAG_LABEL[tag]}</span>
              <div><strong>{p.question}</strong><small>{p.answer.slice(0, 110)}{p.answer.length > 110 ? "…" : ""}</small></div>
              <div className="playlist-actions">
                <button className="icon-btn" data-testid={`playlist-up-${p.pin_id}`} onClick={() => move(i, -1)} disabled={i === 0} title="Move up">↑</button>
                <button className="icon-btn" data-testid={`playlist-down-${p.pin_id}`} onClick={() => move(i, 1)} disabled={i === active.pins.length - 1} title="Move down">↓</button>
                <button className="icon-btn danger" data-testid={`playlist-remove-${p.pin_id}`} onClick={() => removePin(p.pin_id)} title="Remove from playlist"><X size={14} /></button>
              </div>
            </li>; })}
          </ol>}
        </div>
        <div>
          <h4>Add pins ({available.length})</h4>
          {available.length === 0 ? <p className="muted">All your pins are already here — or you have none yet. Pin an answer from Live copilot.</p> : <ul className="playlist-pins available" data-testid="playlist-available">
            {available.map(p => { const tag = p.tag || "behavioural"; const color = PIN_TAGS.find(t => t.key === tag)?.color || "#45d7ff"; return <li key={p.pin_id} data-testid={`playlist-available-${p.pin_id}`}>
              <span className="playlist-tag" style={{ borderColor: color, color }}>{TAG_LABEL[tag]}</span>
              <div><strong>{p.question}</strong><small>{p.answer.slice(0, 110)}{p.answer.length > 110 ? "…" : ""}</small></div>
              <button className="capture-btn" data-testid={`playlist-add-${p.pin_id}`} onClick={() => addPin(p.pin_id)}><Plus size={12} /> Add</button>
            </li>; })}
          </ul>}
        </div>
      </div>
    </section>}
    {rehearsing && active?.pins?.length > 0 && <RehearsalModal playlist={active.playlist} pins={active.pins} onClose={() => setRehearsing(false)} />}
  </main>;
}

const SUPPORT_CATEGORIES = [
  { key: "billing", label: "Payment / Billing" },
  { key: "credits", label: "Credits" },
  { key: "copilot", label: "Interview / Copilot" },
  { key: "audio", label: "Audio / Microphone" },
  { key: "account", label: "Account / Login" },
  { key: "technical", label: "Technical Issue" },
  { key: "other", label: "Other" },
];

function ContactSupport({ user, onClose }) {
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [category, setCategory] = useState("other");
  const [message, setMessage] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const attachRef = useRef(null);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canSubmit = name.trim().length >= 2 && emailValid && message.trim().length >= 10 && !submitting;

  const MAX_BYTES = 5 * 1024 * 1024;
  const ALLOWED = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/gif", "application/pdf"];

  const pickFile = (file) => {
    setError("");
    if (!file) return setAttachment(null);
    const type = (file.type || "").toLowerCase();
    if (!ALLOWED.includes(type)) { setError("Attachment must be PNG, JPG, WEBP, GIF, or PDF"); if (attachRef.current) attachRef.current.value = ""; return; }
    if (file.size > MAX_BYTES) { setError("Attachment must be 5 MB or smaller"); if (attachRef.current) attachRef.current.value = ""; return; }
    setAttachment(file);
  };
  const removeFile = () => { setAttachment(null); if (attachRef.current) attachRef.current.value = ""; };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim() || name.trim().length < 2) return setError("Please enter your full name");
    if (!emailValid) return setError("Please enter a valid email address");
    if (message.trim().length < 10) return setError("Please describe your query (at least 10 characters)");
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("name", name.trim());
      form.append("email", email.trim().toLowerCase());
      form.append("category", category);
      form.append("message", message.trim());
      if (attachment) form.append("attachment", attachment, attachment.name);
      const r = await fetch(`${API}/support/contact`, { method: "POST", credentials: "include", body: form });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not submit your query");
      setSubmitted(true);
      toast.success("Your query has been submitted");
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return <main className="support-page" data-testid="support-page">
      <div className="support-confirm" data-testid="support-confirmation">
        <div className="support-check"><Check size={22} /></div>
        <div className="eyebrow"><span className="live-line" /> QUERY RECEIVED</div>
        <h1>Thank you for contacting <em>Intervia Support.</em></h1>
        <p className="pricing-lead">Your query has been submitted successfully. Our team will review your request and contact you within <strong>24–48 hours</strong>.</p>
        <div className="support-actions">
          {onClose && <button className="primary" data-testid="support-back-button" onClick={onClose}><ArrowLeft size={14} /> Back to Intervia</button>}
          <button className="outline" data-testid="support-new-button" onClick={() => { setSubmitted(false); setMessage(""); setCategory("other"); removeFile(); }}>Submit another query</button>
        </div>
      </div>
    </main>;
  }

  const humanSize = attachment ? (attachment.size >= 1024 * 1024 ? `${(attachment.size / (1024 * 1024)).toFixed(2)} MB` : `${Math.max(1, Math.round(attachment.size / 1024))} KB`) : "";

  return <main className="support-page" data-testid="support-page">
    <div className="eyebrow"><span className="live-line" /> CONTACT SUPPORT</div>
    <h1>We're here to <em>help you win the interview.</em></h1>
    <p className="pricing-lead">Send us your question and we'll get back to you within 24–48 hours. For faster help, include steps you've already tried.</p>

    <form className="support-form" onSubmit={submit} data-testid="support-form" noValidate>
      <div className="support-field">
        <label htmlFor="support-name">Full name <span className="req">*</span></label>
        <input id="support-name" data-testid="support-name-input" value={name} onChange={e => setName(e.target.value)} placeholder="Your full name" required autoComplete="name" />
      </div>
      <div className="support-field">
        <label htmlFor="support-email">Email address <span className="req">*</span></label>
        <input id="support-email" type="email" data-testid="support-email-input" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required autoComplete="email" />
      </div>
      <div className="support-field">
        <label htmlFor="support-category">Query category</label>
        <select id="support-category" data-testid="support-category-select" value={category} onChange={e => setCategory(e.target.value)}>
          {SUPPORT_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
      </div>
      <div className="support-field">
        <label htmlFor="support-message">Query / Message <span className="req">*</span></label>
        <textarea id="support-message" data-testid="support-message-input" value={message} onChange={e => setMessage(e.target.value)} rows={7} maxLength={5000} placeholder="Tell us what's going on. Include screenshot links, order IDs, or anything that helps us help you faster." required />
        <div className="support-counter">{message.length}/5000</div>
      </div>
      <div className="support-field">
        <label>Attachment <span className="optional">optional · PNG / JPG / WEBP / GIF / PDF · max 5 MB</span></label>
        <input ref={attachRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,application/pdf" hidden data-testid="support-attachment-input" onChange={e => pickFile(e.target.files?.[0])} />
        {attachment ? (
          <div className="support-attachment" data-testid="support-attachment-selected">
            <span className="support-attachment-icon"><Paperclip size={14} /></span>
            <div className="support-attachment-meta">
              <strong>{attachment.name}</strong>
              <small>{humanSize} · {attachment.type || "file"}</small>
            </div>
            <button type="button" className="support-attachment-remove" data-testid="support-attachment-remove" onClick={removeFile}><X size={14} /></button>
          </div>
        ) : (
          <button type="button" className="support-attachment-pick" data-testid="support-attachment-pick" onClick={() => attachRef.current?.click()}>
            <Paperclip size={14} /> Attach a screenshot or PDF
          </button>
        )}
      </div>
      {error && <div className="support-error" data-testid="support-error" role="alert">{error}</div>}
      <div className="support-actions">
        <button type="submit" className="primary" data-testid="support-submit-button" disabled={!canSubmit}>{submitting ? "Submitting…" : (<>Submit query <ArrowUpRight size={16} /></>)}</button>
        {onClose && <button type="button" className="outline" data-testid="support-cancel-button" onClick={onClose}><ArrowLeft size={14} /> Cancel</button>}
      </div>
      <p className="mock-note" data-testid="support-privacy-note"><ShieldCheck size={13} /> We reply directly to the email address you provide above. Your message is kept private to the support team.</p>
    </form>
  </main>;
}


const DEMO_EXCHANGES = [
  { q: "Tell me about a time you led a team through a tough deadline.", a: "At my last role I inherited a launch two weeks from ship with three P0 bugs and half the team on leave. I split the group into two pods, ran a 15-min sync at 10 and 17, and personally owned the biggest bug. We shipped on the target date, and the team took the retro to build a proper on-call rotation. It taught me that pressure only compounds if leadership stays quiet." },
  { q: "How would you design a URL shortener that scales to a billion links?", a: "I'd start with a stateless API in front of a globally-replicated key-value store — base62 encoding of a monotonically-increasing shard id, so we never collide. Reads sit behind a CDN edge with a 24-hour TTL because most shortlinks are read-heavy and immutable. For hot links we push counters to Redis and roll them up to Postgres every minute. Analytics goes through a Kafka topic so we can add dashboards without touching the read path." },
  { q: "Why do you want to work here?", a: "Because you're one of the few teams building AI products that respect the human on the other side of the screen — the docs, the pricing, and even the way your engineers write launch posts told me that. I want to work where craft still shows up in the small edges, and where I can push a feature end-to-end without twelve approval gates in the way." },
];

function TypewriterDemo() {
  const [idx, setIdx] = useState(0);
  const [aChars, setAChars] = useState(0);
  const [phase, setPhase] = useState("typing"); // typing | pause | next
  useEffect(() => {
    const answer = DEMO_EXCHANGES[idx].a;
    if (phase === "typing") {
      if (aChars < answer.length) {
        const t = setTimeout(() => setAChars(c => c + Math.max(1, Math.min(3, Math.random() < 0.15 ? 4 : 2))), 22);
        return () => clearTimeout(t);
      }
      setPhase("pause");
    }
    if (phase === "pause") { const t = setTimeout(() => setPhase("next"), 2600); return () => clearTimeout(t); }
    if (phase === "next") { setAChars(0); setIdx((idx + 1) % DEMO_EXCHANGES.length); setPhase("typing"); }
  }, [phase, aChars, idx]);
  const answer = DEMO_EXCHANGES[idx].a.slice(0, aChars);
  return <div className="demo-card" data-testid="landing-demo">
    <div className="demo-head">
      <span className="demo-badge"><span className="live-dot" /> LIVE COPILOT</span>
      <span className="demo-model"><Sparkles size={11} /> GPT-5.4 Mini · Whisper</span>
    </div>
    <div className="demo-question" data-testid="demo-question"><span className="q-tag">Q</span><p>{DEMO_EXCHANGES[idx].q}</p></div>
    <div className="demo-answer" data-testid="demo-answer">
      <div className="a-tag"><span className="signal-bars"><i /><i /><i /></span> Suggested response</div>
      <p>{answer}{phase === "typing" && <span className="cursor" />}</p>
    </div>
    <div className="demo-progress" data-testid="demo-progress">{DEMO_EXCHANGES.map((_, i) => <span key={i} className={i === idx ? "on" : ""} />)}</div>
  </div>;
}

const TESTIMONIALS = [
  { quote: "Landed the offer at Stripe three weeks after starting. Tab-audio capture is the only reason I kept up with the interviewer.", name: "Anika S.", role: "Backend Engineer" },
  { quote: "I stopped forgetting my STAR structure mid-answer. The rehearsal playlist for my Meta loop was worth every rupee.", name: "Rohit K.", role: "Product Manager" },
  { quote: "Set it up on my second screen in five minutes. Passed the phone screen the same afternoon.", name: "Priya M.", role: "iOS Engineer" },
  { quote: "The Whisper transcription is the best part — no lag, and the tagged cheat sheet made revising four rounds trivial.", name: "Kabir A.", role: "Data Scientist" },
];

function Landing({ onTry, onBuy, onSupport }) {
  const navigate = useNavigate();
  const stats = [
    { k: "10", v: "sessions per pass" },
    { k: "≈5s", v: "audio → transcript" },
    { k: "0", v: "monthly subscriptions" },
  ];
  const features = [
    { icon: <Mic size={16} />, title: "Live audio", copy: "Capture your microphone or the interviewer's tab audio. Whisper transcribes every 5 seconds so you're never chasing the question." },
    { icon: <Sparkles size={16} />, title: "GPT-5.4 Mini answers", copy: "Answers stream token-by-token, grounded in your resume and the job description. Under a second to first character." },
    { icon: <Pin size={16} />, title: "Cheat sheet", copy: "Pin your best lines, tag them behavioural / technical / culture, and build named playlists for each company loop." },
    { icon: <Play size={16} />, title: "Focus rehearsal", copy: "Open any playlist in a distraction-free rehearsal view — one card at a time with ← / → keyboard flow." },
  ];
  return <main className="landing" data-testid="landing-page">
    <section className="landing-hero">
      <div className="hero-copy">
        <div className="eyebrow"><span className="live-line" /> REAL-TIME INTERVIEW COPILOT · GPT-5.4 MINI</div>
        <h1>Stay in the moment.<br /><em>Let Intervia think ahead.</em></h1>
        <p className="hero-lead">A quiet second brain that listens to your interview and drafts your next answer — grounded in your resume, tuned to the role, streaming in under a second.</p>

        <div className="choice-cards" data-testid="landing-choice-cards">
          <button className="choice-card try" data-testid="cta-try-free" onClick={() => onTry("hero-try")}>
            <div className="choice-tag">START FREE</div>
            <div className="choice-title">Try Intervia first</div>
            <div className="choice-copy">3 free copilot questions on us. No card, no commitment — feel the streaming, then decide.</div>
            <span className="choice-action">Start free preview <ArrowUpRight size={14} /></span>
          </button>
          <button className="choice-card buy" data-testid="cta-buy-plan" onClick={() => onBuy("hero-buy")}>
            <div className="choice-tag violet">SKIP THE TRIAL</div>
            <div className="choice-title">Buy the ₹4,199 pass</div>
            <div className="choice-copy">10 unlimited-minute sessions, unlimited copilot questions. Straight to Razorpay checkout after sign-in.</div>
            <span className="choice-action">Buy plan directly <ArrowUpRight size={14} /></span>
          </button>
        </div>

        <div className="hero-stats" data-testid="landing-stats">
          {stats.map(s => <div key={s.v}><strong>{s.k}</strong><span>{s.v}</span></div>)}
        </div>
      </div>
      <div className="hero-demo">
        <div className="demo-halo" />
        <TypewriterDemo />
        <div className="demo-caption">Live demo · no sign-in required</div>
      </div>
    </section>

    <section className="landing-features" id="how" data-testid="landing-features">
      <div className="eyebrow"><span className="live-line" /> HOW IT SHOWS UP</div>
      <h2>Every layer of the interview, <em>quietly rehearsed.</em></h2>
      <div className="features-grid">
        {features.map(f => <article key={f.title} className="feature-card">
          <div className="feature-icon">{f.icon}</div>
          <h3>{f.title}</h3>
          <p>{f.copy}</p>
        </article>)}
      </div>
    </section>

    <section className="landing-testimonials" data-testid="landing-testimonials">
      <div className="eyebrow"><span className="live-line" /> WHAT CANDIDATES SAY</div>
      <div className="testimonial-strip">
        {TESTIMONIALS.map(t => <figure key={t.name} className="testimonial-card" data-testid={`testimonial-${t.name.replace(/\W/g, "-")}`}>
          <blockquote>“{t.quote}”</blockquote>
          <figcaption><strong>{t.name}</strong><span>{t.role}</span></figcaption>
        </figure>)}
      </div>
    </section>

    <section className="landing-price">
      <div className="price-copy">
        <div className="eyebrow"><span className="live-line" /> ONE-TIME PASS</div>
        <h2>Buy once. <em>Interview like it's rigged.</em></h2>
        <p>A single ₹4,199 pass unlocks ten unlimited-minute copilot sessions. No subscription, no auto-renewal, no card-on-file surprises. International USD checkout via Stripe is coming next.</p>
        <ul>
          <li><Check size={14} /> 10 interview sessions</li>
          <li><Check size={14} /> Unlimited minutes per session</li>
          <li><Check size={14} /> Cheat sheet + playlists included</li>
          <li><Check size={14} /> Refer a friend, both get +1 session</li>
        </ul>
        <div className="price-cta-row">
          <button className="primary" data-testid="landing-price-buy" onClick={() => onBuy("price-section-buy")}>Buy plan · ₹4,199 <ArrowUpRight size={16} /></button>
          <button className="outline" data-testid="landing-price-try" onClick={() => onTry("price-section-try")}>Try free first</button>
        </div>
      </div>
      <div className="price-figure">
        <div className="figure-inner">
          <span className="figure-label">10-SESSION PASS</span>
          <div className="figure-price">₹4,199</div>
          <span className="figure-note">one-time · India · Razorpay</span>
          <div className="figure-divider" />
          <span className="figure-note-alt">$49.99 · International · <em>coming soon</em></span>
        </div>
      </div>
    </section>

    <footer className="landing-footer">
      <span>© Intervia · Built for candidates who prepare quietly.</span>
      <span className="landing-footer-links">
        <button type="button" className="footer-link" data-testid="landing-footer-about" onClick={() => navigate("/about")}>About the founder</button>
        {onSupport && <button type="button" className="footer-link" data-testid="landing-footer-support" onClick={onSupport}><LifeBuoy size={12} /> Contact support</button>}
        <span>Payments processed by Razorpay · GPT-5.4 Mini via Emergent</span>
      </span>
    </footer>
    <FounderCredit />
  </main>;
}

function FounderCredit() {
  const navigate = useNavigate();
  return <section className="founder-credit" data-testid="founder-credit" aria-label="Founder signature">
    <button type="button" className="founder-portrait-btn" data-testid="founder-portrait-btn" onClick={() => navigate("/about")} aria-label="About the founder">
      <div className="founder-portrait">
        <img src={require("./assets/manpreet.png")} alt="Manpreet Singh — Founder of Intervia" loading="lazy" />
      </div>
      <span className="founder-tooltip" role="tooltip" data-testid="founder-tooltip">
        <strong>Manpreet Singh</strong>
        <em>Founder of Intervia · Building quiet tools that help candidates prepare with dignity.</em>
        <small>Read the story →</small>
      </span>
    </button>
    <p className="founder-line">Created in India with love by <strong>Manpreet Singh</strong></p>
    <p className="founder-tribute">In loving memory of his mother <span aria-hidden="true">❤️</span></p>
  </section>;
}

function AboutFounder() {
  const navigate = useNavigate();
  return <main className="about-page" data-testid="about-page">
    <button className="ghost-action about-back" data-testid="about-back-button" onClick={() => navigate("/")}><ArrowLeft size={13} /> Back to Intervia</button>
    <div className="about-hero">
      <div className="about-portrait">
        <img src={require("./assets/manpreet.png")} alt="Manpreet Singh, founder of Intervia" />
      </div>
      <div className="eyebrow"><span className="live-line" /> A FOUNDER NOTE</div>
      <h1>Built quietly, <em>from a small room in India.</em></h1>
      <p className="about-lede">Hi, I'm <strong>Manpreet Singh</strong> — the person behind Intervia. Thank you for being here.</p>
    </div>

    <article className="about-body" data-testid="about-body">
      <p>Intervia started as a very personal itch. I sat through my own share of interviews where the question was harder than the preparation deserved, and where the answer that lived somewhere in my head refused to come out in the moment. I kept thinking: <em>we don't need a louder coach — we need a quieter one.</em> Something that listens while you talk, thinks a step ahead, and gently hands you the next sentence so you can stay present with the person on the other side of the screen.</p>

      <p>Every part of Intervia is built with that in mind. The transcript that arrives in under five seconds. The answer that streams a token at a time. The cheat sheet that remembers what worked. The rehearsal mode that lets you practise the same three questions until they finally sound like <em>you.</em> No fluff, no dashboards for the sake of dashboards, no subscription trying to hold you hostage. Just a one-time pass and a promise that the product will keep getting quieter and sharper.</p>

      <p>I lost my mother while I was still writing the earliest lines of this product. Anything I do that turns out well, I owe to her — to the patience she taught me and to the way she believed that showing up with dignity mattered more than winning loudly. Intervia carries her fingerprints, even the parts nobody will see. If you land the offer you're preparing for, please take a second and think of her too. That would mean everything.</p>

      <p className="about-signoff">If you'd like to say hi, use <button type="button" className="text-btn about-support-link" data-testid="about-contact-support" onClick={() => navigate("/support")}>Contact support</button> — it lands in my inbox. Wishing you a calm mind and a brave voice.</p>

      <p className="about-signature">— Manpreet</p>
    </article>

    <div className="about-tribute" data-testid="about-tribute" aria-label="Tribute">
      In loving memory of his mother <span aria-hidden="true">❤️</span>
    </div>

    <footer className="landing-footer about-footer">
      <span>© Intervia · Built for candidates who prepare quietly.</span>
      <span className="landing-footer-links">
        <span>Payments processed by Razorpay · GPT-5.4 Mini via Emergent</span>
      </span>
    </footer>
  </main>;
}

const detectMeetingPlatform = (raw) => {
  const url = (raw || "").trim().toLowerCase();
  if (!url) return { key: "none", label: "No link yet" };
  if (/meet\.google\.com/.test(url)) return { key: "meet", label: "Google Meet", browserCapture: true };
  if (/zoom\.us\/j\/|zoom\.us\/wc\/|zoomgov\.com/.test(url)) return { key: "zoom", label: "Zoom", browserCapture: true, dualClient: true };
  if (/teams\.microsoft\.com\/l\/meetup|teams\.live\.com/.test(url)) return { key: "teams", label: "Microsoft Teams", browserCapture: true, dualClient: true };
  if (/whereby\.com|around\.co|jit\.si|hireflix|karat|coderpad|screen\.so|8x8\.vc/.test(url)) return { key: "browser_other", label: "Browser meeting", browserCapture: true };
  return { key: "other", label: "Other / unrecognised", browserCapture: false };
};

const QUESTION_STARTERS = /\b(what|how|why|when|where|who|which|can you|could you|would you|will you|tell me|describe|walk me|explain|give me|do you|are you|have you|is there|should|shall|may|might|give an example)\b/i;
const looksLikeQuestion = (text) => {
  const t = (text || "").trim();
  if (t.length < 12) return false;
  if (t.endsWith("?")) return t.split(/\s+/).length >= 3;
  return QUESTION_STARTERS.test(t) && t.split(/\s+/).length >= 5;
};
const normalizeQ = (t) => t.toLowerCase().replace(/[^a-z0-9\s]/g, "").replace(/\s+/g, " ").trim();
const hashString = (s) => { let h = 0; for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0; return String(h); };

function LiveInterview({ user, resume, job, setJob, onUpload, onGoPricing, refreshUser, session, setSession, startPaidSession, endPaidSession, startingSession, endingSession, sessionsLeft, isPaid, fileRef, onFilePick }) {
  const [meetingLink, setMeetingLink] = useState("");
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [capture, setCapture] = useState("idle");
  const [stt, setStt] = useState("idle");
  const [gpt, setGpt] = useState("idle");
  const [detectedQ, setDetectedQ] = useState("");
  const [answer, setAnswer] = useState("");
  const [history, setHistory] = useState([]);
  const [elapsed, setElapsed] = useState(0);
  const [pipOpen, setPipOpen] = useState(false);

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const activeRef = useRef(false);
  const cycleRef = useRef(null);
  const bufferRef = useRef("");
  const seenRef = useRef(new Set());
  const gptBusyRef = useRef(false);
  const pipWinRef = useRef(null);

  const platform = detectMeetingPlatform(meetingLink);

  useEffect(() => {
    if (capture !== "listening") return;
    const t = setInterval(() => setElapsed(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [capture]);

  const stopCapture = useCallback(() => {
    activeRef.current = false;
    clearTimeout(cycleRef.current);
    try { recorderRef.current?.state === "recording" && recorderRef.current.stop(); } catch {}
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null; recorderRef.current = null;
    setCapture("idle"); setStt("idle");
  }, []);

  const streamAnswerFor = async (q) => {
    if (!user || gptBusyRef.current) return;
    gptBusyRef.current = true;
    setGpt("streaming"); setAnswer("");
    try {
      const r = await fetch(`${API}/copilot/stream`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ question: q, resume_context: resume?.text || "", job_description: job || "", session_id: session }) });
      if (r.status === 402) { const d = await r.json(); setGpt("idle"); gptBusyRef.current = false; toast.error(d.detail || "Upgrade to continue"); return onGoPricing(); }
      if (!r.ok) throw new Error("Copilot unavailable");
      const reader = r.body.getReader(); const decoder = new TextDecoder(); let buffer = ""; let full = "";
      while (true) {
        const { value, done } = await reader.read(); if (done) break;
        buffer += decoder.decode(value, { stream: true }); const lines = buffer.split("\n\n"); buffer = lines.pop();
        lines.forEach(line => { if (!line.startsWith("data: ")) return; const ev = JSON.parse(line.slice(6)); if (ev.type === "start") setSession(ev.session_id); if (ev.type === "delta") { full += ev.content; setAnswer(a => a + ev.content); } });
      }
      setHistory(h => [...h, { q, a: full, at: Date.now() }]);
      await refreshUser();
    } catch (e) { toast.error(e.message); setAnswer("Couldn't reach the copilot. Try again in a moment."); } finally { setGpt("idle"); gptBusyRef.current = false; }
  };

  const onTranscriptChunk = (text) => {
    if (!activeRef.current) return;
    const trimmed = text.replace(/\s+/g, " ").trim();
    if (!trimmed) return;
    bufferRef.current = (bufferRef.current + " " + trimmed).replace(/\s+/g, " ").trim();
    const buf = bufferRef.current;
    const terminator = /[?.!]/.exec(buf);
    const candidate = terminator ? buf.slice(0, terminator.index + 1) : buf;
    if (looksLikeQuestion(candidate)) {
      const h = hashString(normalizeQ(candidate));
      if (seenRef.current.has(h) || gptBusyRef.current) return;
      seenRef.current.add(h);
      bufferRef.current = terminator ? buf.slice(terminator.index + 1).trim() : "";
      setDetectedQ(candidate);
      streamAnswerFor(candidate);
    }
  };

  const uploadChunk = async (blob) => {
    if (!blob || blob.size < 2000) return;
    setStt("processing");
    const form = new FormData(); form.append("file", blob, "chunk.webm");
    try {
      const r = await fetch(`${API}/copilot/transcribe`, { method: "POST", credentials: "include", body: form });
      const d = await r.json();
      if (d.text) onTranscriptChunk(d.text);
    } catch {} finally { setStt("idle"); }
  };

  const cycleRecorder = useCallback(() => {
    if (!activeRef.current || !streamRef.current) return;
    let mime = "audio/webm;codecs=opus"; if (!window.MediaRecorder?.isTypeSupported(mime)) mime = "audio/webm";
    const rec = new MediaRecorder(streamRef.current, { mimeType: mime });
    const chunks = [];
    rec.ondataavailable = e => e.data.size > 0 && chunks.push(e.data);
    rec.onstop = () => { const blob = new Blob(chunks, { type: mime }); uploadChunk(blob); if (activeRef.current) cycleRecorder(); };
    recorderRef.current = rec;
    rec.start();
    cycleRef.current = setTimeout(() => { try { rec.state === "recording" && rec.stop(); } catch {} }, 3500);
  }, []);

  const startLive = async (source) => {
    if (!user) return toast.error("Sign in first");
    if (!isPaid) {
      if (sessionsLeft <= 0) { onGoPricing(); return toast.error("Buy a session pack to start Live Interview"); }
      await startPaidSession();
    }
    setCapture("requesting");
    try {
      const stream = source === "tab"
        ? await navigator.mediaDevices.getDisplayMedia({ audio: true, video: true })
        : await navigator.mediaDevices.getUserMedia({ audio: true });
      if (source === "tab" && !stream.getAudioTracks().length) {
        stream.getTracks().forEach(t => t.stop());
        throw new Error("No tab audio detected — remember to tick 'Share tab audio' in the picker");
      }
      streamRef.current = stream; activeRef.current = true; setCapture("listening");
      stream.getAudioTracks()[0].addEventListener("ended", stopCapture);
      cycleRecorder();
      toast.success(source === "tab" ? "Listening to interviewer tab audio" : "Microphone armed");
      setShowOnboarding(false);
    } catch (e) { setCapture("idle"); toast.error(e.message || "Could not start capture"); }
  };

  const pauseLive = () => {
    if (capture !== "listening") return;
    activeRef.current = false;
    clearTimeout(cycleRef.current);
    try { recorderRef.current?.state === "recording" && recorderRef.current.stop(); } catch {}
    setCapture("paused");
    toast.message("Live copilot paused");
  };

  const resumeLive = () => {
    if (capture !== "paused" || !streamRef.current) return;
    activeRef.current = true; setCapture("listening"); cycleRecorder();
  };

  const stopLive = async () => {
    stopCapture(); setElapsed(0); seenRef.current = new Set(); bufferRef.current = "";
    if (isPaid) { try { await endPaidSession(); } catch {} }
    setDetectedQ(""); setAnswer("Session ended. Your Q&A history for this run is saved above.");
  };

  const openPip = async () => {
    if (!window.documentPictureInPicture) { setPipOpen(true); toast.message("Floating panel unsupported here — showing inline"); return; }
    try {
      const win = await window.documentPictureInPicture.requestWindow({ width: 460, height: 380 });
      pipWinRef.current = win;
      const styleTags = Array.from(document.styleSheets).map(s => { try { return Array.from(s.cssRules).map(r => r.cssText).join("\n"); } catch { return ""; } }).join("\n");
      const style = win.document.createElement("style"); style.textContent = styleTags; win.document.head.appendChild(style);
      win.document.body.className = document.body.className;
      win.document.body.innerHTML = `<div id="pip-root" class="pip-root"></div>`;
      setPipOpen(true);
      win.addEventListener("pagehide", () => { pipWinRef.current = null; setPipOpen(false); });
    } catch (e) { toast.error("Couldn't open floating panel: " + e.message); }
  };

  const closePip = () => { try { pipWinRef.current?.close(); } catch {} pipWinRef.current = null; setPipOpen(false); };

  useEffect(() => {
    if (!pipOpen || !pipWinRef.current) return;
    const w = pipWinRef.current;
    const root = w.document.getElementById("pip-root"); if (!root) return;
    root.innerHTML = "";
    const wrap = w.document.createElement("div"); wrap.className = "pip-wrap";
    wrap.innerHTML = `
      <div class="pip-head">
        <span class="pip-status ${capture === "listening" ? "on" : capture === "paused" ? "pause" : "off"}"></span>
        <strong>Intervia · Live</strong>
        <small>${Math.floor(elapsed/60).toString().padStart(2,"0")}:${(elapsed%60).toString().padStart(2,"0")}</small>
      </div>
      <div class="pip-q"><span>Q</span><p>${(detectedQ || "Waiting for the interviewer's next question…").replace(/</g,"&lt;")}</p></div>
      <div class="pip-a"><span>A</span><p>${(answer || "The suggested answer will stream here.").replace(/</g,"&lt;")}${gpt === "streaming" ? "<span class='cursor'></span>" : ""}</p></div>
      <div class="pip-foot"><em>Capture: ${capture} · STT: ${stt} · GPT: ${gpt}</em></div>
    `;
    root.appendChild(wrap);
  }, [pipOpen, capture, stt, gpt, elapsed, detectedQ, answer]);

  useEffect(() => () => { stopCapture(); try { pipWinRef.current?.close(); } catch {} }, [stopCapture]);

  const timeFmt = `${Math.floor(elapsed/60).toString().padStart(2,"0")}:${(elapsed%60).toString().padStart(2,"0")}`;
  const captureLabel = capture === "listening" ? "LISTENING" : capture === "paused" ? "PAUSED" : capture === "requesting" ? "PERMISSION…" : "STANDBY";

  return <main className="live-page" data-testid="live-interview-page">
    <div className="live-head">
      <div>
        <div className="eyebrow"><span className="live-line" /> LIVE INTERVIEW · REAL-TIME COPILOT</div>
        <h1>Sit calm.<br /><em>Let Intervia listen ahead.</em></h1>
        <p className="head-copy">Whisper transcribes your interviewer, Intervia auto-detects questions and streams the answer before you speak.</p>
      </div>
      <div className="live-badge" data-testid="live-session-status">
        <span className="session-ring" /> {isPaid ? "PAID SESSION" : "PREVIEW"} <small>#{session.slice(-6)}</small>
        <div className="live-time" data-testid="live-time">{timeFmt}</div>
      </div>
    </div>

    <section className="live-context" data-testid="live-context">
      <div className="live-context-item"><FileText size={15} /><div><span>Resume</span><strong data-testid="live-resume-status">{resume ? resume.filename : "Not added"}</strong></div><button className="ghost-action" data-testid="live-resume-upload" onClick={onUpload}><Upload size={13} /> {resume ? "Replace" : "Upload"}</button></div>
      <div className="live-context-item"><Paperclip size={15} /><div className="job-input"><span>Job description</span><input data-testid="live-job-input" value={job} onChange={e => setJob(e.target.value)} placeholder="Paste role or JD…" /></div></div>
      <div className="live-context-item"><Globe2 size={15} /><div className="job-input"><span>Meeting link (optional)</span><input data-testid="live-meeting-input" value={meetingLink} onChange={e => setMeetingLink(e.target.value)} placeholder="Paste your Meet / Zoom / Teams link…" /></div>{meetingLink && <a className="ghost-action" href={meetingLink} target="_blank" rel="noreferrer" data-testid="live-open-meeting"><ArrowUpRight size={13} /> Open</a>}</div>
    </section>

    {meetingLink && <div className="live-platform-hint" data-testid="live-platform-hint">
      <strong>Detected:</strong> {platform.label}
      {platform.dualClient && <span className="hint-warn"> · If this opens as the <strong>desktop app</strong>, tab-audio capture won't reach it. The Intervia Windows Companion is required for that case (arriving next). If you can pick <em>"Join from your browser"</em>, tab-audio capture works.</span>}
      {platform.key === "other" && <span className="hint-warn"> · Unrecognised link. Mic mode still works; interviewer voice only reaches the mic if you're using speakers.</span>}
    </div>}

    <div className="live-hud" data-testid="live-hud">
      <div className={`hud-chip ${capture === "listening" ? "on" : capture === "paused" ? "warn" : ""}`}><Mic size={13} /> Capture <em>{captureLabel}</em></div>
      <div className={`hud-chip ${stt === "processing" ? "on" : ""}`}><AudioLines size={13} /> STT <em>{stt === "processing" ? "TRANSCRIBING" : "READY"}</em></div>
      <div className={`hud-chip ${gpt === "streaming" ? "on" : ""}`}><Sparkles size={13} /> GPT <em>{gpt === "streaming" ? "STREAMING" : "IDLE"}</em></div>
      <div className="hud-chip"><ShieldCheck size={13} /> Credits <em>{sessionsLeft} paid</em></div>
      <button className="ghost-action hud-pip" data-testid="live-pip-toggle" onClick={() => pipOpen ? closePip() : openPip()}>{pipOpen ? "Dock panel" : "Pop out"}</button>
    </div>

    <div className="live-controls" data-testid="live-controls">
      {capture === "idle" && <>
        <button className="primary" data-testid="live-start-tab" onClick={() => platform.dualClient && !meetingLink ? setShowOnboarding(true) : startLive("tab")} disabled={!user || startingSession}><Headphones size={14} /> Start Live · share tab audio</button>
        <button className="outline" data-testid="live-start-mic" onClick={() => startLive("mic")} disabled={!user || startingSession}><Mic size={14} /> Start Live · microphone</button>
        <button className="text-btn" data-testid="live-open-onboarding" onClick={() => setShowOnboarding(true)}>How does the capture work?</button>
      </>}
      {capture === "listening" && <>
        <button className="outline" data-testid="live-pause" onClick={pauseLive}><Square size={14} /> Pause</button>
        <button className="primary stop" data-testid="live-stop" onClick={stopLive}><Square size={14} /> Stop & save session</button>
      </>}
      {capture === "paused" && <>
        <button className="primary" data-testid="live-resume" onClick={resumeLive}><Play size={14} /> Resume</button>
        <button className="outline" data-testid="live-stop-paused" onClick={stopLive}><Square size={14} /> Stop</button>
      </>}
      {capture === "requesting" && <div className="muted">Waiting for permission…</div>}
    </div>

    <div className="live-panels">
      <section className="live-q-card" data-testid="live-question-card">
        <div className="live-kicker"><span className="signal-bars"><i /><i /><i /></span> DETECTED QUESTION</div>
        <p data-testid="live-detected-question">{detectedQ || "Waiting for the interviewer's next question…"}</p>
      </section>
      <section className="live-a-card" data-testid="live-answer-card">
        <div className="live-kicker"><Sparkles size={13} /> SUGGESTED ANSWER</div>
        <p className={gpt === "streaming" ? "streaming" : ""} data-testid="live-answer-text">{answer || "The suggested answer will stream here in under 5 seconds after each question."}{gpt === "streaming" && <span className="cursor" />}</p>
        <div className="live-a-foot">
          <button className="ghost-action" data-testid="live-regenerate" onClick={() => detectedQ && streamAnswerFor(detectedQ)} disabled={!detectedQ || gpt === "streaming"}>Regenerate</button>
          <button className="ghost-action" data-testid="live-copy" onClick={() => { navigator.clipboard?.writeText(answer); toast.success("Answer copied"); }} disabled={!answer}><Copy size={12} /> Copy</button>
          <span className="muted">Context memory active · {history.length} prior Q&A</span>
        </div>
      </section>
    </div>

    {history.length > 0 && <section className="live-history" data-testid="live-history">
      <div className="live-kicker"><ListMusic size={13} /> THIS SESSION · {history.length} question{history.length === 1 ? "" : "s"}</div>
      <ol>
        {history.slice().reverse().map((row, i) => <li key={row.at} data-testid={`live-history-${i}`}>
          <strong>Q · {row.q}</strong>
          <small>A · {row.a.slice(0, 260)}{row.a.length > 260 ? "…" : ""}</small>
        </li>)}
      </ol>
    </section>}

    {showOnboarding && <div className="modal-scrim" onClick={() => setShowOnboarding(false)} data-testid="live-onboarding-scrim">
      <div className="onboarding-modal" onClick={e => e.stopPropagation()} data-testid="live-onboarding-modal">
        <button className="auth-modal-close" data-testid="live-onboarding-close" onClick={() => setShowOnboarding(false)}><X size={16} /></button>
        <div className="eyebrow"><span className="live-line" /> HOW LIVE CAPTURE WORKS</div>
        <h2>Two ways Intervia hears the interviewer</h2>
        <div className="onboarding-grid">
          <div className="onboarding-card">
            <h3><Headphones size={15} /> Share tab audio <em>(best for headphones)</em></h3>
            <p>Works for <strong>Google Meet</strong>, <strong>Zoom web client</strong>, <strong>Teams web client</strong>, Whereby and other browser meetings.</p>
            <ol>
              <li>Join the interview in a Chrome or Edge tab.</li>
              <li>Return here, click <strong>Start Live · share tab audio</strong>.</li>
              <li>In the picker, pick <strong>Chrome Tab</strong>, choose the meeting tab, then <strong>tick "Share tab audio"</strong>.</li>
              <li>Intervia hears the interviewer directly from the tab. Headphones/earbuds are fine.</li>
            </ol>
          </div>
          <div className="onboarding-card">
            <h3><Mic size={15} /> Microphone <em>(only if you're on speakers)</em></h3>
            <p>Works with any meeting — including <strong>Zoom desktop</strong> and <strong>Teams desktop</strong> — but only if the interviewer's voice is audible to your microphone (you're on speakers, not headphones).</p>
            <p>If you're on headphones and using the Zoom or Teams desktop app, wait for the Intervia Windows Companion (Windows-first, arriving next). It captures system audio directly with your permission.</p>
          </div>
        </div>
        <div className="onboarding-foot">
          <button className="primary" data-testid="onboarding-start-tab" onClick={() => startLive("tab")}><Headphones size={14} /> Share tab audio</button>
          <button className="outline" data-testid="onboarding-start-mic" onClick={() => startLive("mic")}><Mic size={14} /> Use microphone</button>
        </div>
      </div>
    </div>}
  </main>;
}



function Workspace({ user, onUser }) {
  const [view, setView] = useState(() => {
    const intent = typeof window !== "undefined" ? sessionStorage.getItem("intervia_intent") : null;
    if (intent === "buy") { sessionStorage.removeItem("intervia_intent"); return "pricing"; }
    return "live";
  });
  const [signupIntent] = useState(() => (typeof window !== "undefined" && sessionStorage.getItem("intervia_intent_seen") === "buy") ? "buy" : (view === "pricing" ? (sessionStorage.setItem("intervia_intent_seen", "buy"), "buy") : "try"));
  const [mode, setMode] = useState("text");
  const [question, setQuestion] = useState("");
  const [job, setJob] = useState("");
  const [resume, setResume] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [answer, setAnswer] = useState("Your next answer will stream here as soon as you send an interview question.");
  const [streaming, setStreaming] = useState(false);
  const [session, setSession] = useState("session_demo");
  const [startingSession, setStartingSession] = useState(false);
  const [endingSession, setEndingSession] = useState(false);
  const [receiptOrder, setReceiptOrder] = useState(null);
  const [lastQuestion, setLastQuestion] = useState("");
  const [pinning, setPinning] = useState(false);
  const [pinTag, setPinTag] = useState("behavioural");
  const fileRef = useRef(null);
  // Continuous-listen refs: accumulate transcript across chunks, auto-detect questions, dedupe already-answered ones.
  const liveBufferRef = useRef("");
  const seenQsRef = useRef(new Set());
  const gptBusyRef = useRef(false);

  const refreshUser = useCallback(async () => { try { const r = await fetch(`${API}/auth/me`, { credentials: "include" }); if (r.ok) { const d = await r.json(); onUser(d); if (d.active_session_id) setSession(d.active_session_id); return d; } } catch {} return null; }, [onUser]);
  useEffect(() => { refreshUser(); }, [refreshUser]);

  const startPaidSession = async () => {
    if (!user) return toast.error("Sign in first");
    if ((user.sessions_remaining || 0) <= 0) { setView("pricing"); return toast.error("Buy a session pack to start a live session"); }
    setStartingSession(true);
    try {
      const r = await fetch(`${API}/sessions/start`, { method: "POST", credentials: "include" });
      const d = await r.json(); if (!r.ok) throw new Error(d.detail || "Could not start session");
      setSession(d.session_id); setTranscript([]); setAnswer("New paid session started. Ask your first question below.");
      liveBufferRef.current = ""; seenQsRef.current = new Set();
      await refreshUser(); toast.success(`Live session started · ${d.sessions_remaining} left`);
    } catch (e) { toast.error(e.message); } finally { setStartingSession(false); }
  };

  const endPaidSession = async () => {
    setEndingSession(true);
    try {
      const r = await fetch(`${API}/sessions/end`, { method: "POST", credentials: "include" });
      const d = await r.json(); if (!r.ok) throw new Error(d.detail || "Could not end session");
      setSession("session_demo"); setTranscript([]); setAnswer("Session ended. Start a new paid session anytime.");
      liveBufferRef.current = ""; seenQsRef.current = new Set();
      await refreshUser(); toast.success("Paid session ended");
    } catch (e) { toast.error(e.message); } finally { setEndingSession(false); }
  };

  const streamAnswer = async (q) => {
    if (gptBusyRef.current) return;
    gptBusyRef.current = true;
    setLastQuestion(q);
    setStreaming(true); setAnswer(""); setTranscript(t => [...t, { from: "you", text: q }]);
    try {
      const r = await fetch(`${API}/copilot/stream`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ question: q, resume_context: resume?.text || "", job_description: job, session_id: session }) });
      if (r.status === 402) { const d = await r.json(); setAnswer(""); setStreaming(false); setView("pricing"); return toast.error(d.detail || "Upgrade to continue"); }
      if (!r.ok) throw new Error("Copilot unavailable");
      const reader = r.body.getReader(); const decoder = new TextDecoder(); let buffer = "";
      while (true) { const { value, done } = await reader.read(); if (done) break; buffer += decoder.decode(value, { stream: true }); const lines = buffer.split("\n\n"); buffer = lines.pop(); lines.forEach(line => { if (!line.startsWith("data: ")) return; const event = JSON.parse(line.slice(6)); if (event.type === "start") setSession(event.session_id); if (event.type === "delta") setAnswer(a => a + event.content); }); }
      setTranscript(t => [...t, { from: "copilot", text: "Answer drafted from your context." }]);
      await refreshUser();
    } catch (e) { toast.error(e.message); setAnswer("Couldn't reach the copilot. Try again in a moment."); } finally { setStreaming(false); gptBusyRef.current = false; }
  };

  const send = async () => {
    if (!question.trim()) return toast.error("Add an interview question first");
    if (!user) return toast.error("Sign in to activate your copilot");
    const q = question; setQuestion(""); await streamAnswer(q);
  };

  const onLiveTranscript = (text) => {
    const clean = (text || "").replace(/\s+/g, " ").trim();
    if (!clean) return;
    // Always mirror into the manual transcript feed for user visibility.
    setTranscript(t => [...t, { from: "interviewer", text: clean }]);
    // Continuous auto-answer: buffer chunks, detect a completed question, auto-fire GPT, dedupe.
    liveBufferRef.current = (liveBufferRef.current + " " + clean).replace(/\s+/g, " ").trim();
    const buf = liveBufferRef.current;
    const terminator = /[?.!]/.exec(buf);
    const candidate = terminator ? buf.slice(0, terminator.index + 1) : buf;
    if (!looksLikeQuestion(candidate)) {
      // Keep the current text visible in the manual question box as before, but drop the "append forever" behaviour so the box stays readable.
      setQuestion(candidate);
      return;
    }
    const h = hashString(normalizeQ(candidate));
    if (seenQsRef.current.has(h) || gptBusyRef.current || !user) {
      // Already answered or GPT busy — keep buffering silently; will retry after current stream ends.
      return;
    }
    seenQsRef.current.add(h);
    liveBufferRef.current = terminator ? buf.slice(terminator.index + 1).trim() : "";
    setQuestion("");
    streamAnswer(candidate);
  };

  const upload = async file => { if (!file) return; if (!user) return toast.error("Sign in to save resume context"); const form = new FormData(); form.append("file", file); const r = await fetch(`${API}/uploads/resume`, { method: "POST", credentials: "include", body: form }); const data = await r.json(); if (!r.ok) return toast.error(data.detail || "Upload failed"); setResume(data); toast.success("Resume context added"); };
  const logout = async () => { await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" }); onUser(null); toast.success("Signed out"); };

  const pinAnswer = async () => {
    if (!user) return toast.error("Sign in to pin answers");
    if (!lastQuestion || !answer || streaming) return toast.error("Ask a question and let the answer finish first");
    if (answer.startsWith("Your next answer") || answer.startsWith("New paid session") || answer.startsWith("Session ended")) return toast.error("Nothing to pin yet");
    setPinning(true);
    try {
      const r = await fetch(`${API}/pins`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ question: lastQuestion, answer, session_id: session, tag: pinTag }) });
      const d = await r.json(); if (!r.ok) throw new Error(d.detail || "Could not pin");
      toast.success(`Pinned to ${TAG_LABEL[pinTag]}`);
    } catch (e) { toast.error(e.message); } finally { setPinning(false); }
  };

  const isPaid = session.startsWith("session_paid_");
  const freeUsed = user?.free_credits_used ?? 0; const sessionsLeft = user?.sessions_remaining ?? 0;

  return <div className="app-shell">
    <header className="topbar">
      <div className="brand" data-testid="brand-logo"><span className="brand-icon"><Zap size={17} /></span><span>Intervia</span></div>
      <div className="top-actions">
        <span className="status-dot"><i /> {user ? (isPaid ? "Live paid session" : "Workspace live") : "Preview mode"}</span>
        {user && <span className="credits-chip" data-testid="credits-chip"><ShieldCheck size={13} /> {sessionsLeft} paid · {Math.max(0, 3 - freeUsed)} free left</span>}
        {user && <button className="ghost-action" data-testid="header-support-button" onClick={() => setView("support")}><LifeBuoy size={13} /> Support</button>}
        {user ? <><span className="user-chip" data-testid="current-user"><UserRound size={14} /> {user.name}</span><button className="icon-btn" data-testid="logout-button" title="Sign out" onClick={logout}><LogOut size={16} /></button></> : <span className="plan-chip" data-testid="plan-status">Free preview</span>}
      </div>
    </header>
    <div className="main-layout">
      <nav className="sidebar" data-testid="workspace-sidebar">
        <div className="nav-group">
          <div className="nav-label">Workspace</div>
          <button className={view === "live" ? "nav-item nav-item-featured active" : "nav-item nav-item-featured"} data-testid="nav-live-interview" onClick={() => setView("live")}><Zap size={17} /> <span className="nav-item-label">Live Interview</span> <span className="nav-new nav-new-live">NEW</span></button>
        </div>
        <div className="nav-group">
          <div className="nav-label">Tools</div>
          <button className={view === "pricing" ? "nav-item nav-item-purchase active" : "nav-item nav-item-purchase"} data-testid="nav-pricing" onClick={() => setView("pricing")}><Sparkles size={17} /> <span className="nav-item-label">Session Packs</span> <span className="nav-new nav-new-price">₹4,199</span></button>
          <button className={view === "cheatsheet" ? "nav-item active" : "nav-item"} data-testid="nav-cheatsheet" onClick={() => setView("cheatsheet")}><Pin size={17} /> <span className="nav-item-label">Cheat Sheet</span></button>
          <button className={view === "playlists" ? "nav-item active" : "nav-item"} data-testid="nav-playlists" onClick={() => setView("playlists")}><ListMusic size={17} /> <span className="nav-item-label">Playlists</span></button>
        </div>
        <div className="nav-group">
          <div className="nav-label">Account</div>
          <button className={view === "billing" ? "nav-item active" : "nav-item"} data-testid="nav-billing" onClick={() => setView("billing")}><Receipt size={17} /> <span className="nav-item-label">Billing</span></button>
          <button className={view === "referrals" ? "nav-item active" : "nav-item"} data-testid="nav-referrals" onClick={() => setView("referrals")}><Gift size={17} /> <span className="nav-item-label">Refer & Earn</span></button>
          <button className={view === "support" ? "nav-item active" : "nav-item"} data-testid="nav-support" onClick={() => setView("support")}><LifeBuoy size={17} /> <span className="nav-item-label">Contact Support</span></button>
        </div>
        <div className="sidebar-bottom">
          <div className="mini-pulse"><span><AudioLines size={16} /></span><div><strong>Whisper transcription live</strong><small>Mic + tab audio, chunked every 5 s</small></div></div>
          {!user && <div className="signin-note" data-testid="signin-note"><strong>Unlock your context</strong><small>Sign in to save resumes and sessions</small></div>}
        </div>
      </nav>
      {view === "pricing" && <Pricing user={user} onPurchased={refreshUser} signupIntent={signupIntent} />}
      {view === "billing" && <BillingHub user={user} onOpenReceipt={setReceiptOrder} />}
      {view === "referrals" && <ReferralPanel user={user} />}
      {view === "cheatsheet" && <CheatSheet user={user} />}
      {view === "playlists" && <Playlists user={user} />}
      {view === "support" && <ContactSupport user={user} onClose={() => setView("live")} />}
      {view === "live" && <LiveInterview user={user} resume={resume} job={job} setJob={setJob} onUpload={() => fileRef.current?.click()} onGoPricing={() => setView("pricing")} refreshUser={refreshUser} session={session} setSession={setSession} startPaidSession={startPaidSession} endPaidSession={endPaidSession} startingSession={startingSession} endingSession={endingSession} sessionsLeft={sessionsLeft} isPaid={isPaid} fileRef={fileRef} onFilePick={upload} />}
      {view === "workspace" && <main className="workspace">
        <div className="workspace-head">
          <div>
            <div className="eyebrow"><span className="live-line" /> REAL-TIME INTERVIEW COPILOT</div>
            <h1>Stay in the moment.<br /><em>Let Intervia think ahead.</em></h1>
            <p className="head-copy">Your quiet second brain for technical and behavioral interviews.</p>
          </div>
          <div className="session-badge" data-testid="session-status">
            <span className="session-ring" /> {isPaid ? "PAID SESSION" : "PREVIEW SESSION"} <small>#{session.slice(-6)}</small>
            {user && !isPaid && sessionsLeft > 0 && <button className="ghost-mini" data-testid="start-paid-session-button" disabled={startingSession} onClick={startPaidSession}>{startingSession ? "Starting…" : "Start paid session"}</button>}
            {user && isPaid && <button className="ghost-mini stop-mini" data-testid="end-paid-session-button" disabled={endingSession} onClick={endPaidSession}>{endingSession ? "Ending…" : "End session"}</button>}
            {user && !isPaid && sessionsLeft === 0 && <button className="ghost-mini" data-testid="upgrade-inline-button" onClick={() => setView("pricing")}><Lock size={12} /> Unlock more</button>}
          </div>
        </div>
        <section className="context-strip">
          <div className="context-item"><div className="context-icon cyan"><FileText size={17} /></div><div><span>Resume context</span><strong data-testid="resume-status">{resume ? resume.filename : "Not added yet"}</strong></div><button className="small-action" data-testid="resume-upload-button" onClick={() => fileRef.current?.click()}><Upload size={14} /> {resume ? "Replace" : "Upload"}</button><input ref={fileRef} type="file" accept=".txt,.pdf,.doc,.docx" hidden data-testid="resume-file-input" onChange={e => upload(e.target.files[0])} /></div>
          <div className="context-divider" />
          <div className="context-item job-item"><div className="context-icon violet"><Paperclip size={17} /></div><div className="job-input"><span>Target role / job description</span><input data-testid="job-description-input" value={job} onChange={e => setJob(e.target.value)} placeholder="Paste a role or description…" /></div>{job && <Check className="context-check" size={16} />}</div>
        </section>
        <div className="mode-tabs">
          <button className={mode === "text" ? "mode-tab selected" : "mode-tab"} data-testid="text-mode-tab" onClick={() => setMode("text")}><FileText size={15} /> Text demo</button>
          <button className={mode === "live" ? "mode-tab selected" : "mode-tab"} data-testid="live-mode-tab" onClick={() => setMode("live")}><Mic size={15} /> Live audio</button>
          <span className="mode-hint">{mode === "text" ? "Type an interview question below" : "Capture your mic or the interviewer's tab audio"}</span>
        </div>
        {mode === "live" && <LiveCapture onTranscript={onLiveTranscript} disabled={!user} />}
        <div className="copilot-grid">
          <section className="transcript-panel">
            <div className="panel-head"><div><span className="panel-kicker">01 / LISTENING</span><h2>Live transcript</h2></div><span className="listening-state"><i /> {mode === "text" ? "Text input" : "Whisper live"}</span></div>
            <div className="transcript-body" data-testid="transcript-feed">{transcript.length === 0 ? <div className="empty-transcript"><div className="waveform"><i /><i /><i /><i /><i /><i /><i /></div><p>Ask a question below<br /><span>or start live capture to auto-fill</span></p></div> : transcript.map((line, i) => <div className={`transcript-line ${line.from}`} key={i}><span>{line.from === "you" ? "YOU" : line.from === "interviewer" ? "INTERVIEWER" : "INTERVIA"}</span><p>{line.text}</p></div>)}</div>
            <div className="question-box"><textarea data-testid="interview-question-input" value={question} onChange={e => setQuestion(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder={mode === "text" ? "Type an interview question…" : "Auto-populates from live capture — tweak & send"} /><button className="send-btn" data-testid="send-question-button" onClick={send}>{streaming ? <Square size={15} /> : <ArrowUpRight size={17} />}</button><div className="question-meta"><span><Sparkles size={12} /> GPT-5.4 Mini</span><span>↵ send</span></div></div>
          </section>
          <section className="answer-panel">
            <div className="panel-head"><div><span className="panel-kicker">02 / RESPOND</span><h2>Answer signal</h2></div><span className="ai-badge"><Sparkles size={13} /> AI READY</span></div>
            <div className="answer-body" data-testid="answer-card"><div className="answer-label"><span className="signal-bars"><i /><i /><i /></span>Suggested response</div><p className={streaming ? "answer-text streaming" : "answer-text"}>{answer}{streaming && <span className="cursor" />}</p>{answer && !answer.startsWith("Your next answer") && !answer.startsWith("New paid session") && <div className="coach-note" data-testid="coach-note"><Sparkles size={14} /><div><strong>Coach note</strong><span>Keep your opening direct, then anchor it with one specific example.</span></div></div>}</div>
            <div className="answer-footer"><select className="tag-select inline" data-testid="pin-tag-picker" value={pinTag} onChange={e => setPinTag(e.target.value)}>{PIN_TAGS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}</select><button className="ghost-action" data-testid="pin-answer-button" onClick={pinAnswer} disabled={pinning || streaming}><BookmarkPlus size={13} /> {pinning ? "Pinning…" : "Pin"}</button><button className="ghost-action" data-testid="copy-answer-button" onClick={() => { navigator.clipboard?.writeText(answer); toast.success("Answer copied"); }}>Copy answer</button><span>Built for clarity under pressure</span></div>
          </section>
        </div>
      </main>}
    </div>
    {receiptOrder && <ReceiptModal orderId={receiptOrder} onClose={() => setReceiptOrder(null)} />}
    <Toaster theme="dark" position="bottom-right" />
  </div>;
}

function Pricing({ user, onPurchased, signupIntent }) {
  const [region, setRegion] = useState("IN");
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [config, setConfig] = useState(null);
  useEffect(() => { if (!user) return; fetch(`${API}/payments/config`, { credentials: "include" }).then(r => r.ok ? r.json() : null).then(setConfig).catch(() => {}); }, [user]);

  const startCheckout = async () => {
    if (!user) return toast.error("Sign in to purchase");
    const buyIntent = signupIntent === "buy" ? "buy" : "try";
    setCheckoutBusy(true);
    try {
      const ok = await loadRazorpayScript(); if (!ok) throw new Error("Payment SDK failed to load");
      const orderRes = await fetch(`${API}/payments/razorpay/order`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ currency: "INR" }) });
      const order = await orderRes.json(); if (!orderRes.ok) throw new Error(order.detail || "Could not create order");
      track(buyIntent === "buy" ? "checkout_opened_buy_intent" : "checkout_opened_try_intent", { order_id: order.order_id });
      const rzp = new window.Razorpay({
        key: order.key_id, amount: order.amount, currency: order.currency, order_id: order.order_id, name: order.name, description: order.description, prefill: order.prefill, theme: { color: "#00e6b8" },
        handler: async (resp) => {
          try {
            const v = await fetch(`${API}/payments/razorpay/verify`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ razorpay_order_id: resp.razorpay_order_id, razorpay_payment_id: resp.razorpay_payment_id, razorpay_signature: resp.razorpay_signature }) });
            const vd = await v.json(); if (!v.ok) throw new Error(vd.detail || "Verification failed");
            track(buyIntent === "buy" ? "checkout_paid_buy_intent" : "checkout_paid_try_intent", { order_id: resp.razorpay_order_id, sessions_remaining: vd.sessions_remaining });
            toast.success(`Payment confirmed · ${vd.sessions_remaining} sessions unlocked`); await onPurchased();
          } catch (e) { toast.error(e.message); }
        },
        modal: { ondismiss: () => toast.message("Checkout closed") }
      });
      rzp.on("payment.failed", (r) => toast.error(r.error?.description || "Payment failed"));
      rzp.open();
    } catch (e) { toast.error(e.message); } finally { setCheckoutBusy(false); }
  };

  const inrDisplay = config?.packages?.INR?.display || "₹4,199";
  const usdDisplay = config?.packages?.USD?.display || "$49.99";

  return <main className="pricing-page">
    <div className="eyebrow"><span className="live-line" /> ONE-TIME PASS · 10 SESSIONS</div>
    <h1>Buy once. <em>Interview like it's rigged.</em></h1>
    <p className="pricing-lead">A single pass unlocks ten unlimited-minute copilot sessions. No subscription, no auto-renewal.</p>
    <div className="region-toggle" role="tablist" data-testid="region-toggle">
      <button className={region === "IN" ? "region-btn active" : "region-btn"} data-testid="region-in" onClick={() => setRegion("IN")}><Globe2 size={13} /> India · INR</button>
      <button className={region === "INT" ? "region-btn active" : "region-btn"} data-testid="region-int" onClick={() => setRegion("INT")}><Globe2 size={13} /> International · USD</button>
    </div>
    <div className="pricing-grid">
      <div className="price-card">
        <span className="price-label">FREE PREVIEW</span>
        <h2>₹0 <small>/ no card needed</small></h2>
        <p>Test the copilot on us before you commit.</p>
        <ul>
          <li><Check size={15} /> 3 preview copilot questions</li>
          <li><Check size={15} /> Resume + role context saved</li>
          <li><Check size={15} /> Text-mode streaming answers</li>
        </ul>
        <button className="outline wide" data-testid="free-plan-button" disabled>Included with your account</button>
      </div>
      <div className="price-card featured">
        <div className="popular">10-SESSION PASS</div>
        <span className="price-label">INTERVIEW PASS</span>
        {region === "IN" ? <h2 data-testid="price-display">{inrDisplay} <small>/ one-time</small></h2> : <h2 data-testid="price-display">{usdDisplay} <small>/ one-time</small></h2>}
        <p>{region === "IN" ? "Charged in INR via Razorpay. UPI, cards, netbanking, wallets." : "International card checkout is arriving soon. Pay in INR today from anywhere."}</p>
        <ul>
          <li><Check size={15} /> 10 interview sessions</li>
          <li><Check size={15} /> Unlimited minutes per session</li>
          <li><Check size={15} /> Unlimited copilot questions per session</li>
          <li><Check size={15} /> Whisper transcription (mic + tab audio)</li>
        </ul>
        {region === "IN" ? <button className="primary wide" data-testid="razorpay-checkout-button" onClick={startCheckout} disabled={checkoutBusy || !user}>{checkoutBusy ? "Opening checkout…" : (<>Pay {inrDisplay} with Razorpay <ArrowUpRight size={16} /></>)}</button> : <button className="outline wide" data-testid="usd-coming-soon-button" disabled><Lock size={14} /> International cards · coming soon</button>}
        {region === "INT" && <button className="text-btn" data-testid="switch-to-inr-button" onClick={() => setRegion("IN")}>Or pay {inrDisplay} in INR from anywhere →</button>}
      </div>
    </div>
    <p className="mock-note" data-testid="checkout-note"><ShieldCheck size={13} /> Payments processed by Razorpay. International USD card checkout via Stripe is on the roadmap — no charges are hidden or recurring.</p>
    {user && <p className="mock-note" data-testid="balance-note"><Zap size={13} /> You currently hold {user.sessions_remaining ?? 0} paid session{(user.sessions_remaining ?? 0) === 1 ? "" : "s"}.</p>}
  </main>;
}

function LoggedOutShell({ onAuth }) {
  const [showAuth, setShowAuth] = useState(false);
  const [intent, setIntent] = useState("try"); // "try" | "buy"
  const [showSupport, setShowSupport] = useState(false);
  const params = new URLSearchParams(window.location.search);
  const ref = params.get("ref") || "";
  const urlIntent = params.get("intent");
  useEffect(() => {
    if (urlIntent === "buy") { setIntent("buy"); setShowAuth(true); track("landing_view_buy_intent", { source: "url" }); }
    else { track("landing_view"); }
    if (ref) { setShowAuth(true); track("landing_view", { via: "ref" }); }
    if (window.location.pathname === "/support" || params.get("support") === "1") { setShowSupport(true); }
  }, [ref, urlIntent]);
  const openAuth = (nextIntent, source) => { setIntent(nextIntent); setShowAuth(true); track(nextIntent === "buy" ? "buy_cta_click" : "try_cta_click", { source }); if (nextIntent === "buy") track("landing_view_buy_intent", { source }); };
  const handleAuth = (u) => { track(intent === "buy" ? "signup_buy_intent" : "signup_try_intent", { user_id: u?.user_id }); if (intent === "buy") sessionStorage.setItem("intervia_intent", "buy"); onAuth(u); };
  const openSupport = (source) => { setShowSupport(true); setShowAuth(false); track("support_view", { source }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const closeSupport = () => { setShowSupport(false); window.scrollTo({ top: 0, behavior: "smooth" }); };
  return <div className="app-shell logged-out">
    <header className="topbar minimal">
      <div className="brand" data-testid="brand-logo"><span className="brand-icon"><Zap size={17} /></span><span>Intervia</span></div>
      <div className="top-actions">
        {!showSupport && <a className="ghost-action" href="#how" data-testid="landing-nav-how">How it works</a>}
        <button className="ghost-action" data-testid="landing-nav-support" onClick={() => showSupport ? closeSupport() : openSupport("nav-support")}><LifeBuoy size={13} /> {showSupport ? "Back" : "Contact support"}</button>
        {!showSupport && <button className="ghost-action" data-testid="landing-nav-signin" onClick={() => openAuth("try", "nav-signin")}>Sign in <ChevronRight size={14} /></button>}
        {!showSupport && <button className="primary" data-testid="landing-nav-buy" onClick={() => openAuth("buy", "nav-buy")}>Buy plan · ₹4,199</button>}
      </div>
    </header>
    {showSupport ? <ContactSupport onClose={closeSupport} /> : <>
      <Landing onTry={(src) => openAuth("try", src)} onBuy={(src) => openAuth("buy", src)} onSupport={() => openSupport("landing-footer")} />
      {showAuth && <div className="auth-modal-scrim" data-testid="auth-modal-scrim" onClick={() => setShowAuth(false)}>
        <div className="auth-modal-wrap" onClick={e => e.stopPropagation()}>
          <button className="auth-modal-close" data-testid="auth-modal-close" onClick={() => setShowAuth(false)}><X size={16} /></button>
          {intent === "buy" && <div className="auth-modal-banner" data-testid="auth-modal-banner"><Zap size={13} /> Buying the ₹4,199 pass — you'll land straight on checkout after sign-in.</div>}
          <AuthPanel onAuth={handleAuth} defaultReferralCode={ref} />
        </div>
      </div>}
    </>}
    <Toaster theme="dark" position="bottom-right" />
  </div>;
}

function AppRouter() {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { fetch(`${API}/auth/me`, { credentials: "include" }).then(r => r.ok ? r.json() : null).then(d => { setUser(d); setLoading(false); }).catch(() => setLoading(false)); }, []);
  if (location.hash?.includes("session_id=")) return <AuthCallback onAuth={setUser} />;
  if (loading) return <div className="boot" data-testid="boot-loading"><Sparkles size={16} /> Warming up your workspace…</div>;
  if (location.pathname === "/about") return <AboutRoute />;
  if (location.pathname === "/interview-copilot") return <InterviewCopilotRoute />;
  if (!user) return <LoggedOutShell onAuth={setUser} />;
  return <Workspace user={user} onUser={setUser} />;
}

function InterviewCopilotRoute() {
  const navigate = useNavigate();
  return <div className="app-shell logged-out">
    <header className="topbar minimal">
      <div className="brand" data-testid="brand-logo" onClick={() => navigate("/")}><span className="brand-icon"><Zap size={17} /></span><span>Intervia</span></div>
      <div className="top-actions">
        <a className="ghost-action" href="/" data-testid="copilot-nav-home">Home</a>
        <a className="ghost-action" href="/about" data-testid="copilot-nav-about">About</a>
        <a className="ghost-action" href="/support" data-testid="copilot-nav-support"><LifeBuoy size={13} /> Support</a>
        <button className="primary" data-testid="copilot-nav-cta" onClick={() => navigate("/?intent=try")}>Try Intervia</button>
      </div>
    </header>
    <InterviewCopilotPage />
    <Toaster theme="dark" position="bottom-right" />
  </div>;
}

function InterviewCopilotPage() {
  const navigate = useNavigate();

  useEffect(() => {
    const prev = { title: document.title };
    document.title = "Interview Copilot | AI Interview Assistant | Intervia";
    // Mutate the existing global tags in place so there is exactly ONE of each on this page.
    // Save originals and restore on cleanup — never append duplicate meta/canonical tags.
    const PAGE_DESC = "Intervia is an AI interview copilot for interview preparation and real-time assistance using your resume, job description, questions, and live transcription.";
    const PAGE_URL = "https://getintervia.tech/interview-copilot";
    const PAGE_OG_TITLE = "Interview Copilot | AI Interview Assistant | Intervia";
    const PAGE_TW_DESC = "AI interview copilot for real-time interview assistance. Resume + JD grounded. Live audio transcription.";
    const mutate = (selector, attr, value) => {
      const el = document.head.querySelector(selector);
      if (!el) return null;
      const original = el.getAttribute(attr);
      el.setAttribute(attr, value);
      return { el, attr, original };
    };
    // Also proactively remove any stray duplicate tags (defensive — in case a previous
    // build left behind data-copilot="1" clones during hot reload).
    document.head.querySelectorAll('[data-copilot="1"]').forEach(n => n.remove());
    const mutations = [
      mutate('meta[name="description"]', "content", PAGE_DESC),
      mutate('link[rel="canonical"]', "href", PAGE_URL),
      mutate('meta[property="og:title"]', "content", PAGE_OG_TITLE),
      mutate('meta[property="og:description"]', "content", PAGE_DESC),
      mutate('meta[property="og:url"]', "content", PAGE_URL),
      mutate('meta[name="twitter:title"]', "content", PAGE_OG_TITLE),
      mutate('meta[name="twitter:description"]', "content", PAGE_TW_DESC),
    ].filter(Boolean);
    // WebPage JSON-LD (additive schema type — not a duplicate meta/canonical)
    const ld = document.createElement("script");
    ld.type = "application/ld+json"; ld.setAttribute("data-copilot", "1");
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Interview Copilot | AI Interview Assistant | Intervia",
      url: PAGE_URL,
      description: PAGE_DESC,
      isPartOf: { "@type": "WebSite", name: "Intervia", url: "https://getintervia.tech/" }
    });
    document.head.appendChild(ld);
    window.scrollTo({ top: 0 });
    return () => {
      document.title = prev.title;
      mutations.forEach(({ el, attr, original }) => {
        if (original === null) el.removeAttribute(attr);
        else el.setAttribute(attr, original);
      });
      ld.remove();
    };
  }, []);

  const goTry = () => navigate("/?intent=try");
  const goBuy = () => navigate("/?intent=buy");

  return <main className="copilot-page" data-testid="interview-copilot-page">
    <section className="copilot-hero">
      <div className="eyebrow"><span className="live-line" /> INTERVIEW COPILOT · REAL-TIME AI</div>
      <h1 data-testid="copilot-h1">Interview Copilot for Real-Time AI Interview Assistance</h1>
      <p className="copilot-lede">Intervia is an AI interview copilot designed to help job candidates prepare for interviews and get real-time assistance during supported online interviews. Add your resume and job description, practice your responses, and use AI-generated suggestions to help you organise your answers when you need them.</p>
      <div className="copilot-cta-row">
        <button className="primary" data-testid="copilot-cta-try" onClick={goTry}><Zap size={14} /> Try Intervia</button>
        <a className="outline" href="#how-it-works" data-testid="copilot-cta-how">See how it works <ChevronRight size={14} /></a>
      </div>
    </section>

    <section className="copilot-section">
      <h2>What is an interview copilot?</h2>
      <p>An <strong>interview copilot</strong> is an AI-assisted tool that helps candidates prepare for interviews and, where the meeting platform allows, provides real-time assistance while the interview is happening. Instead of replacing the candidate, it acts as a quiet second brain — surfacing context from your resume, the job description, and the current question so you can answer with confidence.</p>
      <p>Intervia is a real-time <em>AI interview assistant</em>: it listens (with your permission) to supported browser meetings, transcribes the interviewer's question, and streams a concise suggested answer grounded in your own material. You always decide what to say.</p>
    </section>

    <section className="copilot-section" id="how-it-works">
      <h2>How Intervia works</h2>
      <ol className="copilot-steps">
        <li><strong>Add your resume.</strong> Upload the PDF or DOCX you'll be interviewing with. Intervia extracts the plain text for context.</li>
        <li><strong>Add the job description.</strong> Paste the JD (or a summary of it) so answers can lean into the exact role.</li>
        <li><strong>Prepare before the interview.</strong> Use the text mode to rehearse likely questions and pin the answers you like into your cheat sheet.</li>
        <li><strong>Start Live Interview.</strong> When the interview begins, open Intervia's Live Interview view and share the meeting tab audio (or use your microphone).</li>
        <li><strong>Review AI-generated suggestions.</strong> Intervia auto-detects a completed question, streams a suggested answer within a couple of seconds, and keeps a running history for the session.</li>
      </ol>
    </section>

    <section className="copilot-section">
      <h2>What can an AI interview copilot help with?</h2>
      <ul className="copilot-checklist">
        <li><strong>Interview preparation.</strong> Rehearse the questions you expect for the specific role.</li>
        <li><strong>Resume-based preparation.</strong> Answers are shaped by the experience you actually have.</li>
        <li><strong>Job-description context.</strong> Language and priorities of the target role are folded in.</li>
        <li><strong>Real-time interview assistance.</strong> Where the meeting platform supports it, a suggested answer streams while you're still gathering your thoughts.</li>
        <li><strong>Answer organisation.</strong> A structure suggestion helps you avoid rambling.</li>
        <li><strong>Interview practice.</strong> A private, judgement-free space to try the same answer three or four different ways.</li>
      </ul>
    </section>

    <section className="copilot-section">
      <h2>Intervia Live Interview</h2>
      <p>Live Interview is Intervia's real-time flow. The signal path is:</p>
      <p className="copilot-flow"><strong>Meeting audio</strong> <span>→</span> <strong>Speech-to-text (Whisper)</strong> <span>→</span> <strong>Question detection</strong> <span>→</span> <strong>AI response generation (GPT-5.4 Mini)</strong> <span>→</span> <strong>Suggested answer</strong> <span>→</span> <strong>Candidate review</strong></p>
      <p><em>A note on audio capture, so there are no surprises:</em> audio capabilities depend on your browser, operating system, meeting platform, and the permissions you grant. In Chrome or Edge, Intervia can capture the interviewer's voice from a shared meeting tab (Google Meet, Zoom Web, Microsoft Teams Web, Whereby, and similar browser meetings) — this works even when you're wearing headphones, because the audio is drawn directly from the tab. Some desktop meeting apps (for example, the Zoom desktop app or the Microsoft Teams desktop app on Windows or macOS) can't be captured by a browser at all; supporting those reliably needs additional compatibility, and a Windows Companion for that case is on the way. Intervia never joins the interview on your behalf.</p>
    </section>

    <section className="copilot-section">
      <h2>Who is Intervia for?</h2>
      <div className="copilot-cards">
        <div className="copilot-card"><h3>Job seekers</h3><p>Anyone actively applying who wants a calmer, better-prepared interview loop.</p></div>
        <div className="copilot-card"><h3>Career changers</h3><p>Especially useful when the vocabulary of a new industry doesn't yet come naturally.</p></div>
        <div className="copilot-card"><h3>Remote interview candidates</h3><p>Almost every interview happens on Meet, Zoom, or Teams now — Intervia is built for that world.</p></div>
        <div className="copilot-card"><h3>Professionals preparing for important interviews</h3><p>Senior IC, engineering-manager, PM, or leadership interviews where the stakes are unusually high.</p></div>
      </div>
    </section>

    <section className="copilot-section">
      <h2>Interview copilot vs. traditional interview preparation</h2>
      <div className="copilot-table-wrap">
        <table className="copilot-table" data-testid="copilot-comparison-table">
          <thead><tr><th></th><th>Traditional preparation</th><th>Intervia</th></tr></thead>
          <tbody>
            <tr><td>Approach</td><td>Manual practice</td><td>AI-assisted practice</td></tr>
            <tr><td>Context</td><td>Generic preparation</td><td>Resume + job description context</td></tr>
            <tr><td>Notes</td><td>Scattered across docs and tabs</td><td>Centralised workspace</td></tr>
            <tr><td>Review</td><td>Manual review after the fact</td><td>Session Q&A history</td></tr>
            <tr><td>During the interview</td><td>No live AI assistance</td><td>Live Interview assistance (supported platforms)</td></tr>
            <tr><td>Answer style</td><td>Generic templates</td><td>Context-aware suggestions</td></tr>
          </tbody>
        </table>
      </div>
      <p className="copilot-note"><em>Note: Intervia assists your preparation and response process. It does not replace your own judgement, experience, or communication — the answers you give are still yours.</em></p>
    </section>

    <section className="copilot-section">
      <h2>Why use your resume and job description?</h2>
      <p>When Intervia has your <strong>resume</strong>, suggested answers can draw on real projects, technologies, and outcomes you've actually shipped — instead of generic filler. When Intervia also has the <strong>job description</strong>, it can lean into the exact vocabulary and priorities the hiring team cares about (e.g. "reliability at scale" for an SRE role, or "consultative selling" for an account-executive role). The combination gives you answers that feel like you on your best day, tuned to the role you're interviewing for.</p>
    </section>

    <section className="copilot-section">
      <h2>Interview copilot for customer-service interviews</h2>
      <p>Customer-service interviews often revolve around a familiar set of behavioural and situational questions. Intervia helps you rehearse the ones below by drafting a first-person answer that uses your own resume — you can then refine, shorten, or reword it until it sounds like you.</p>
      <ul className="copilot-checklist">
        <li>Tell me about your customer service experience.</li>
        <li>How would you handle an angry customer?</li>
        <li>Tell me about a difficult customer situation.</li>
        <li>How do you prioritise multiple customer issues?</li>
        <li>Which CRM or support tools have you used?</li>
        <li>When would you escalate a customer issue?</li>
      </ul>
    </section>

    <section className="copilot-section">
      <h2>Frequently asked questions</h2>
      <div className="copilot-faq" data-testid="copilot-faq">
        <details><summary>What is an interview copilot?</summary><p>An AI-assisted tool that helps you prepare for interviews and, where the meeting platform allows, provides real-time answer suggestions during a live interview.</p></details>
        <details><summary>How does an AI interview copilot work?</summary><p>It listens to (or reads) each interviewer question, transcribes it, and asks a language model to draft a candidate-friendly answer using your resume and the job description as context. You review the suggestion and answer in your own words.</p></details>
        <details><summary>Can Intervia help me prepare for an interview?</summary><p>Yes. Add your resume, paste the job description, and rehearse likely questions in text mode. You can pin the answers you like into a cheat sheet and group them into playlists for focused practice.</p></details>
        <details><summary>Can Intervia provide real-time interview assistance?</summary><p>Yes, on supported browser-based interviews (Chrome or Edge). Intervia captures the meeting-tab audio or your microphone, transcribes the question, and streams a suggested answer within a couple of seconds.</p></details>
        <details><summary>Does Intervia work with Google Meet?</summary><p>Yes. Join Meet in a Chrome or Edge tab, start Intervia's Live Interview, share the Meet tab and tick "Share tab audio". Intervia hears the interviewer directly from the tab — headphones are fine.</p></details>
        <details><summary>Does Intervia work with Zoom?</summary><p>The Zoom web client (Chrome / Edge) works the same way as Google Meet — share the tab audio. The Zoom desktop app is not currently supported inside the browser; a Windows Companion for that case is arriving.</p></details>
        <details><summary>Does Intervia automatically join my interview?</summary><p>No. Intervia never joins, dials into, or controls your interview. You start the meeting yourself in your normal client; Intervia only listens to the audio you explicitly grant.</p></details>
        <details><summary>Does Intervia write my answers for me?</summary><p>Intervia drafts a suggested answer for each detected question. It's a starting point — you always decide what to say and how to say it.</p></details>
        <details><summary>Can I use my resume with Intervia?</summary><p>Yes. Upload your resume (PDF or DOCX) once, and Intervia will draw on it for the rest of the session. It's stored securely and only used to ground your own answers.</p></details>
        <details><summary>Is an AI interview copilot a replacement for interview preparation?</summary><p>No — it augments preparation, it doesn't replace it. The strongest interviews are still the ones you've thought through in advance. Intervia just gives you a calmer, better-organised place to do that thinking, and a safety net during the interview itself.</p></details>
      </div>
    </section>

    <section className="copilot-final">
      <div className="eyebrow"><span className="live-line" /> READY TO PRACTISE</div>
      <h2>Start preparing for your next interview</h2>
      <p>Add your resume, paste the job description, rehearse a few questions in text mode, and — when the interview arrives — use Live Interview on a supported browser to get real-time answer suggestions.</p>
      <div className="copilot-cta-row">
        <button className="primary" data-testid="copilot-final-cta-try" onClick={goTry}><Zap size={14} /> Try Intervia</button>
        <a className="outline" href="/support" data-testid="copilot-final-cta-support"><LifeBuoy size={14} /> Contact support</a>
      </div>
      <p className="copilot-links">Related: <a href="/" data-testid="copilot-link-home">Intervia home</a> · <a href="/about" data-testid="copilot-link-about">About the founder</a> · <a href="/support" data-testid="copilot-link-support">Contact support</a></p>
    </section>
  </main>;
}

function AboutRoute() {
  return <div className="app-shell logged-out">
    <header className="topbar minimal">
      <div className="brand" data-testid="brand-logo"><span className="brand-icon"><Zap size={17} /></span><span>Intervia</span></div>
    </header>
    <AboutFounder />
    <Toaster theme="dark" position="bottom-right" />
  </div>;
}
export default function App() { return <BrowserRouter><AppRouter /></BrowserRouter>; }
